import React from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

  .bi-page * { box-sizing: border-box; margin: 0; padding: 0; }
  .bi-page { font-family: 'Inter', sans-serif; color: #0f172a; background: #ffffff; width: 100%; }

  /* Hero */
  .bi-hero { position: relative; padding: 100px 24px 120px; overflow: hidden; background: #fff; }
  .bi-hero-blob1 { position: absolute; top: -10%; right: -5%; width: 500px; height: 500px; border-radius: 50%; background: radial-gradient(circle, rgba(199,210,254,0.6), rgba(221,214,254,0.3)); filter: blur(80px); pointer-events: none; }
  .bi-hero-blob2 { position: absolute; bottom: -10%; left: -10%; width: 600px; height: 600px; border-radius: 50%; background: radial-gradient(circle, rgba(186,230,253,0.5), rgba(167,243,208,0.3)); filter: blur(80px); pointer-events: none; }
  .bi-hero-inner { max-width: 1200px; margin: 0 auto; display: grid; grid-template-columns: 1fr 1fr; gap: 64px; align-items: center; position: relative; z-index: 1; }
  @media (max-width: 900px) { .bi-hero-inner { grid-template-columns: 1fr; } .bi-hero-card { display: none; } }
  .bi-hero-back { margin-bottom: 20px; }
  .bi-back-btn { display: inline-flex; align-items: center; gap: 6px; padding: 6px 14px; border-radius: 999px; border: 1px solid #e2e8f0; background: #ffffff; color: #475569; font-size: 12px; font-weight: 600; cursor: pointer; transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease; }
  .bi-back-btn:hover { background: #f8fafc; border-color: #c7d2fe; color: #4f46e5; }
  .bi-badge { display: inline-flex; align-items: center; gap: 8px; padding: 8px 16px; border-radius: 999px; background: #f8fafc; border: 1px solid #e2e8f0; font-size: 13px; font-weight: 500; color: #475569; margin-bottom: 28px; }
  .bi-badge-dot { width: 8px; height: 8px; border-radius: 50%; background: #6366f1; display: inline-block; }
  .bi-hero h1 { font-size: clamp(1.875rem, 3.75vw, 3.15rem); font-weight: 900; line-height: 1.12; letter-spacing: -0.03em; color: #0f172a; margin-bottom: 24px; }
  .bi-gradient-text { background: linear-gradient(135deg, #4f46e5, #0ea5e9); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
  .bi-hero p { font-size: 1.2rem; color: #475569; line-height: 1.75; margin-bottom: 40px; font-weight: 300; }
  .bi-btn-group { display: flex; gap: 16px; flex-wrap: wrap; }
  .bi-btn-primary { padding: 14px 28px; background: #1e293b; color: #fff; border: none; border-radius: 12px; font-size: 15px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 8px; transition: background 0.2s; }
  .bi-btn-primary:hover { background: #4f46e5; }
  .bi-btn-secondary { padding: 14px 28px; background: #fff; color: #334155; border: 1px solid #e2e8f0; border-radius: 12px; font-size: 15px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
  .bi-btn-secondary:hover { border-color: #c7d2fe; background: #f8f9ff; }

  /* Hero Card */
  .bi-hero-card { background: #fff; border-radius: 24px; border: 1px solid #e2e8f0; padding: 32px; box-shadow: 0 20px 60px rgba(79,70,229,0.08); position: relative; }
  .bi-card-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 28px; padding-bottom: 20px; border-bottom: 1px solid #f1f5f9; }
  .bi-card-title p:first-child { font-size: 13px; color: #94a3b8; font-weight: 500; margin-bottom: 4px; }
  .bi-card-title p:last-child { font-size: 17px; font-weight: 700; color: #0f172a; }
  .bi-card-icon { width: 48px; height: 48px; background: #dcfce7; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 20px; }
  .bi-policy-item { display: flex; justify-content: space-between; align-items: center; padding: 14px 16px; border-radius: 12px; background: #f8fafc; border: 1px solid #f1f5f9; margin-bottom: 10px; }
  .bi-policy-left { display: flex; align-items: center; gap: 10px; font-size: 14px; font-weight: 600; color: #334155; }
  .bi-policy-dot { width: 8px; height: 8px; border-radius: 50%; }
  .bi-policy-right { font-size: 13px; color: #94a3b8; font-weight: 500; }
  .bi-card-cta { margin-top: 24px; padding-top: 20px; border-top: 1px solid #f1f5f9; }
  .bi-card-cta-btn { width: 100%; padding: 14px; background: #eef2ff; color: #4f46e5; border: none; border-radius: 10px; font-size: 14px; font-weight: 600; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px; transition: background 0.2s; }
  .bi-card-cta-btn:hover { background: #e0e7ff; }

  /* Section base */
  .bi-section { padding: 96px 24px; }
  .bi-container { max-width: 1200px; margin: 0 auto; }
  .bi-section-header { text-align: center; max-width: 720px; margin: 0 auto 64px; }
  .bi-section-header h2 { font-size: clamp(1.8rem, 3.5vw, 2.8rem); font-weight: 800; color: #0f172a; letter-spacing: -0.02em; margin-bottom: 16px; line-height: 1.2; }
  .bi-section-header p { font-size: 1.1rem; color: #64748b; line-height: 1.75; }

  /* Stats */
  .bi-stats { background: #fff; border-top: 1px solid #f1f5f9; border-bottom: 1px solid #f1f5f9; padding: 0; }
  .bi-stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); max-width: 1200px; margin: 0 auto; }
  @media (max-width: 768px) { .bi-stats-grid { grid-template-columns: 1fr 1fr; } }
  .bi-stat-item { padding: 36px 24px; text-align: center; border-right: 1px solid #f1f5f9; }
  .bi-stat-item:last-child { border-right: none; }
  @media (max-width: 768px) { .bi-stat-item:nth-child(2n) { border-right: none; } .bi-stat-item { border-bottom: 1px solid #f1f5f9; } }
  .bi-stat-num { font-size: 2rem; font-weight: 900; color: #4f46e5; letter-spacing: -0.03em; line-height: 1; margin-bottom: 8px; }
  .bi-stat-label { font-size: 0.82rem; color: #64748b; font-weight: 500; line-height: 1.4; }

  /* Value Props */
  .bi-props { background: #f8fafc; border-top: 1px solid rgba(226,232,240,0.5); border-bottom: 1px solid rgba(226,232,240,0.5); }
  .bi-props-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 32px; }
  @media (max-width: 768px) { .bi-props-grid { grid-template-columns: 1fr; } }
  .bi-prop-card { background: #fff; padding: 36px; border-radius: 20px; border: 1px solid rgba(226,232,240,0.6); box-shadow: 0 1px 4px rgba(0,0,0,0.03); transition: all 0.3s; position: relative; overflow: hidden; }
  .bi-prop-card:hover { box-shadow: 0 16px 48px rgba(0,0,0,0.08); transform: translateY(-2px); }
  .bi-prop-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px; opacity: 0; transition: opacity 0.3s; }
  .bi-prop-card:hover::before { opacity: 1; }
  .bi-prop-card.amber::before { background: linear-gradient(90deg, #f59e0b, #ef4444); }
  .bi-prop-card.indigo::before { background: linear-gradient(90deg, #4f46e5, #7c3aed); }
  .bi-prop-card.emerald::before { background: linear-gradient(90deg, #10b981, #14b8a6); }
  .bi-prop-icon { width: 56px; height: 56px; border-radius: 14px; display: flex; align-items: center; justify-content: center; margin-bottom: 24px; font-size: 24px; }
  .bi-prop-icon.amber { background: #fff7ed; }
  .bi-prop-icon.indigo { background: #eef2ff; }
  .bi-prop-icon.emerald { background: #f0fdf4; }
  .bi-prop-card h3 { font-size: 1.2rem; font-weight: 700; color: #1e293b; margin-bottom: 14px; }
  .bi-prop-card p { font-size: 0.95rem; color: #64748b; line-height: 1.7; }

  /* Verticals */
  .bi-verticals { background: #fff; }
  .bi-vert-header { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 48px; gap: 24px; flex-wrap: wrap; }
  .bi-vert-header h2 { font-size: clamp(1.8rem, 3.5vw, 2.6rem); font-weight: 800; color: #0f172a; letter-spacing: -0.02em; max-width: 520px; }
  .bi-vert-header p { font-size: 1rem; color: #64748b; line-height: 1.7; max-width: 400px; }
  .bi-vert-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
  @media (max-width: 768px) { .bi-vert-grid { grid-template-columns: 1fr; } }
  .bi-vert-card { display: flex; gap: 20px; padding: 28px; border-radius: 20px; background: #f8fafc; border: 1px solid #f1f5f9; transition: all 0.3s; cursor: pointer; }
  .bi-vert-card:hover { background: #fff; box-shadow: 0 12px 40px rgba(0,0,0,0.08); border-color: #c7d2fe; }
  .bi-vert-icon { flex-shrink: 0; width: 56px; height: 56px; border-radius: 16px; background: #fff; border: 1px solid #e2e8f0; display: flex; align-items: center; justify-content: center; font-size: 24px; transition: all 0.3s; }
  .bi-vert-card:hover .bi-vert-icon { background: #eef2ff; border-color: #c7d2fe; }
  .bi-vert-card h3 { font-size: 1.1rem; font-weight: 700; color: #1e293b; margin-bottom: 8px; }
  .bi-vert-card p { font-size: 0.9rem; color: #64748b; line-height: 1.65; margin-bottom: 16px; }
  .bi-tag-list { display: flex; flex-wrap: wrap; gap: 8px; }
  .bi-tag { padding: 4px 12px; font-size: 12px; font-weight: 600; border-radius: 999px; background: rgba(226,232,240,0.5); color: #475569; transition: all 0.2s; }
  .bi-vert-card:hover .bi-tag { background: #eef2ff; color: #4f46e5; }

  /* Dark Coverage Section */
  .bi-coverage { background: #0f172a; color: #f8fafc; position: relative; overflow: hidden; }
  .bi-coverage-glow1 { position: absolute; top: 0; right: 25%; width: 600px; height: 600px; background: radial-gradient(circle, rgba(79,70,229,0.25), transparent 70%); pointer-events: none; }
  .bi-coverage-glow2 { position: absolute; bottom: -20%; left: -10%; width: 500px; height: 500px; background: radial-gradient(circle, rgba(14,165,233,0.15), transparent 70%); pointer-events: none; }
  .bi-coverage h2 { color: #fff; }
  .bi-coverage .bi-section-header p { color: #94a3b8; }
  .bi-cov-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
  @media (max-width: 900px) { .bi-cov-grid { grid-template-columns: 1fr 1fr; } }
  @media (max-width: 600px) { .bi-cov-grid { grid-template-columns: 1fr; } }
  .bi-cov-card { background: rgba(30,41,59,0.4); border: 1px solid rgba(71,85,105,0.4); border-radius: 20px; padding: 32px; transition: border-color 0.3s; }
  .bi-cov-card:hover { border-color: rgba(99,102,241,0.5); }
  .bi-cov-icon { font-size: 28px; margin-bottom: 20px; display: block; transition: transform 0.2s; }
  .bi-cov-card:hover .bi-cov-icon { transform: scale(1.15); }
  .bi-cov-card h4 { font-size: 1.05rem; font-weight: 700; color: #fff; margin-bottom: 10px; }
  .bi-cov-card p { font-size: 0.9rem; color: #94a3b8; line-height: 1.7; }

  /* Light variant for coverage cards used on white sections */
  .bi-cov-card.light { background: #ffffff; border: 1px solid #e2e8f0; }
  .bi-cov-card.light h4 { color: #0f172a; }
  .bi-cov-card.light p { color: #475569; }

  /* Proactive Risk */
  .bi-proactive { background: #fff; }
  .bi-proactive-inner { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 32px; padding: 64px; display: flex; gap: 80px; align-items: center; flex-wrap: wrap; }
  @media (max-width: 900px) { .bi-proactive-inner { padding: 40px; gap: 48px; } }
  .bi-proactive-left { flex: 1; min-width: 280px; }
  .bi-proactive-left h2 { font-size: clamp(1.8rem, 3vw, 2.8rem); font-weight: 800; color: #0f172a; letter-spacing: -0.02em; margin-bottom: 16px; line-height: 1.2; }
  .bi-proactive-left > p { font-size: 1.05rem; color: #64748b; line-height: 1.75; margin-bottom: 40px; }
  .bi-feature-list { display: flex; flex-direction: column; gap: 32px; }
  .bi-feature { display: flex; gap: 16px; }
  .bi-feature-icon { flex-shrink: 0; width: 48px; height: 48px; border-radius: 14px; display: flex; align-items: center; justify-content: center; font-size: 20px; }
  .bi-feature-icon.sky { background: #e0f2fe; }
  .bi-feature-icon.amber { background: #fef3c7; }
  .bi-feature-icon.emerald { background: #dcfce7; }
  .bi-feature h4 { font-size: 1.05rem; font-weight: 700; color: #1e293b; margin-bottom: 6px; }
  .bi-feature p { font-size: 0.92rem; color: #64748b; line-height: 1.65; }
  .bi-proactive-right { flex: 1; min-width: 280px; }
  .bi-visual-card { background: linear-gradient(145deg, #4f46e5, #0ea5e9); border-radius: 24px; padding: 40px; position: relative; overflow: hidden; min-height: 380px; display: flex; flex-direction: column; justify-content: space-between; }
  .bi-visual-dots { position: absolute; inset: 0; opacity: 0.15; background-image: radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 0); background-size: 24px 24px; pointer-events: none; }
  .bi-visual-testimonial { background: rgba(255,255,255,0.1); backdrop-filter: blur(8px); border: 1px solid rgba(255,255,255,0.2); border-radius: 16px; padding: 20px; display: flex; gap: 12px; align-items: flex-start; }
  .bi-visual-dot { width: 10px; height: 10px; border-radius: 50%; background: #4ade80; box-shadow: 0 0 10px rgba(74,222,128,0.8); flex-shrink: 0; margin-top: 4px; }
  .bi-visual-testimonial p { font-size: 13px; color: #fff; font-weight: 500; line-height: 1.6; }
  .bi-visual-status { background: #fff; border-radius: 16px; padding: 20px; box-shadow: 0 8px 32px rgba(0,0,0,0.15); margin-top: 20px; }
  .bi-visual-status-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 14px; }
  .bi-visual-status-header span:first-child { font-weight: 700; color: #1e293b; font-size: 14px; }
  .bi-visual-status-badge { font-size: 11px; font-weight: 700; color: #4f46e5; background: #eef2ff; padding: 4px 10px; border-radius: 999px; text-transform: uppercase; letter-spacing: 0.05em; }
  .bi-progress-bar { height: 8px; background: #f1f5f9; border-radius: 999px; overflow: hidden; margin-bottom: 12px; }
  .bi-progress-fill { height: 100%; width: 100%; background: #4f46e5; border-radius: 999px; }
  .bi-visual-status p { font-size: 12px; color: #94a3b8; line-height: 1.5; }

  /* CTA */
  .bi-cta { background: #fff; border-top: 1px solid #f1f5f9; }
  .bi-cta-inner { background: #0f172a; border-radius: 32px; padding: 80px 48px; text-align: center; position: relative; overflow: hidden; max-width: 1000px; margin: 0 auto; }
  @media (max-width: 600px) { .bi-cta-inner { padding: 48px 24px; } }
  .bi-cta-glow1 { position: absolute; top: 0; right: 0; width: 350px; height: 350px; background: radial-gradient(circle, rgba(99,102,241,0.35), transparent 70%); pointer-events: none; }
  .bi-cta-glow2 { position: absolute; bottom: 0; left: 0; width: 350px; height: 350px; background: radial-gradient(circle, rgba(14,165,233,0.2), transparent 70%); pointer-events: none; }
  .bi-cta-inner h2 { font-size: clamp(2rem, 4vw, 3rem); font-weight: 900; color: #fff; letter-spacing: -0.03em; line-height: 1.15; margin-bottom: 20px; position: relative; z-index: 1; }
  .bi-cta-inner p { font-size: 1.15rem; color: #94a3b8; font-weight: 300; max-width: 560px; margin: 0 auto 40px; line-height: 1.7; position: relative; z-index: 1; }
  .bi-cta-btns { display: flex; gap: 16px; justify-content: center; flex-wrap: wrap; position: relative; z-index: 1; }
  .bi-cta-primary { padding: 15px 32px; background: #6366f1; color: #fff; border: none; border-radius: 12px; font-size: 16px; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; gap: 8px; transition: background 0.2s; box-shadow: 0 4px 20px rgba(99,102,241,0.4); }
  .bi-cta-primary:hover { background: #818cf8; }
  .bi-cta-secondary { padding: 15px 32px; background: #1e293b; color: #fff; border: 1px solid #334155; border-radius: 12px; font-size: 16px; font-weight: 700; cursor: pointer; transition: all 0.2s; }
  .bi-cta-secondary:hover { background: #334155; }

  /* Industry detail pages */
  .bi-industry { background: #f8fafc; }
  .bi-industry-inner { max-width: 1100px; margin: 0 auto; padding: 72px 24px 88px; }
  .bi-industry-header { margin-bottom: 48px; display: grid; grid-template-columns: minmax(0, 2fr) minmax(0, 1.4fr); gap: 40px; align-items: flex-start; }
  @media (max-width: 900px) { .bi-industry-header { grid-template-columns: 1fr; } }
  .bi-industry-kicker { font-size: 13px; font-weight: 600; letter-spacing: 0.09em; text-transform: uppercase; color: #6366f1; margin-bottom: 10px; }
  .bi-industry-title { font-size: 2rem; font-weight: 800; color: #0f172a; margin-bottom: 14px; letter-spacing: -0.02em; }
  .bi-industry-body { font-size: 0.98rem; color: #64748b; line-height: 1.75; }
  .bi-pill-list { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 8px; }
  .bi-pill { padding: 4px 10px; border-radius: 999px; background: #e0f2fe; color: #0369a1; font-size: 11px; font-weight: 600; }
  .bi-industry-section { margin: 0 0 40px; padding: 32px 0 24px; }
  .bi-industry-section--light { background: #ffffff; }
  .bi-industry-section--muted { background: #f9fafb; }
  .bi-industry-section h3 { font-size: 1.5rem; font-weight: 800; color: #2563eb; margin-bottom: 18px; letter-spacing: -0.02em; }
  .bi-industry-two-col { display: grid; grid-template-columns: minmax(0, 1.2fr) minmax(0, 1.2fr); gap: 28px; }
  @media (max-width: 900px) { .bi-industry-two-col { grid-template-columns: 1fr; } }
  .bi-risk-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 14px; }
  .bi-risk-item-title { font-weight: 600; font-size: 0.96rem; color: #0f172a; margin-bottom: 2px; }
  .bi-risk-item-body { font-size: 0.9rem; color: #64748b; }
  .bi-coverage-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 18px; }
  @media (max-width: 900px) { .bi-coverage-grid { grid-template-columns: 1fr 1fr; } }
  @media (max-width: 640px) { .bi-coverage-grid { grid-template-columns: 1fr; } }
  .bi-coverage-chip { font-size: 11px; font-weight: 600; color: #4f46e5; text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 4px; }
  .bi-coverage-card { background: #ffffff; border-radius: 16px; border: 1px solid #e2e8f0; padding: 16px 18px; box-shadow: 0 1px 2px rgba(15,23,42,0.04); transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease; }
  .bi-coverage-card:hover { transform: translateY(-4px); box-shadow: 0 14px 35px rgba(15,23,42,0.12); border-color: #c7d2fe; }
  .bi-coverage-card h4 { font-size: 0.98rem; font-weight: 700; color: #0f172a; margin-bottom: 6px; }
  .bi-coverage-card p { font-size: 0.88rem; color: #64748b; line-height: 1.6; }
  .bi-scenarios { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 18px; }
  @media (max-width: 900px) { .bi-scenarios { grid-template-columns: 1fr 1fr; } }
  @media (max-width: 640px) { .bi-scenarios { grid-template-columns: 1fr; } }
  .bi-scenario { background: #0f172a; color: #e5e7eb; border-radius: 18px; padding: 18px 18px 16px; position: relative; overflow: hidden; transition: transform 0.18s ease, box-shadow 0.18s ease; box-shadow: 0 10px 30px rgba(15,23,42,0.45); }
  .bi-scenario:hover { transform: translateY(-3px) scale(1.01); box-shadow: 0 18px 45px rgba(15,23,42,0.65); }
  .bi-scenario-label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.1em; color: #a5b4fc; margin-bottom: 6px; }
  .bi-scenario h4 { font-size: 0.98rem; font-weight: 700; margin-bottom: 6px; color: #e5e7eb; }
  .bi-scenario p { font-size: 0.86rem; line-height: 1.6; color: #cbd5f5; }
  .bi-scenario strong { color: #e0e7ff; font-weight: 600; }
  .bi-faq { border-top: 1px solid #e2e8f0; padding-top: 24px; margin-top: 8px; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 18px; }
  @media (max-width: 900px) { .bi-faq { grid-template-columns: 1fr; } }
  .bi-faq-item h4 { font-size: 0.96rem; font-weight: 700; color: #0f172a; margin-bottom: 4px; }
  .bi-faq-item p { font-size: 0.88rem; color: #64748b; line-height: 1.6; }
`;

const fadeIn = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-50px" },
  transition: { duration: 0.6 }
};

interface HeroProps {
  showBack?: boolean;
  onBack?: () => void;
  title?: string;
  subtitle?: string;
  primaryCta?: string;
  secondaryCta?: string;
}

const Hero: React.FC<HeroProps> = ({
  showBack,
  onBack,
  title,
  subtitle,
  primaryCta,
  secondaryCta,
}) => (
  <section className="bi-hero">
    <div className="bi-hero-blob1" />
    <div className="bi-hero-blob2" />
    <div className="bi-hero-inner">
      <div>
        {showBack && (
          <div className="bi-hero-back">
            <button
              type="button"
              onClick={onBack}
              className="bi-back-btn"
            >
              <span aria-hidden="true">←</span>
              Back
            </button>
          </div>
        )}
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}>
          <div className="bi-badge">
            <span className="bi-badge-dot" />
            Business insurance in a dash — from quote to coverage in clicks, not weeks
          </div>
        </motion.div>
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}>
          {title || (
            <>
              Business insurance that keeps you{' '}
              <span className="bi-gradient-text">moving forward</span>.
            </>
          )}
        </motion.h1>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
          {subtitle ||
            "BizServe delivers instant, tailored business insurance backed by 30+ top‑rated carriers — from your first startup policy to enterprise‑grade programs. Tell us what you do, select your policy, and get covered in minutes."}
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }} className="bi-btn-group">
          <button className="bi-btn-primary">
            {primaryCta || "Get a business insurance quote →"}
          </button>
          <button className="bi-btn-secondary">
            {secondaryCta || "Talk to an insurance advisor"}
          </button>
        </motion.div>
      </div>

      <motion.div className="bi-hero-card" initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.4 }}>
        <div className="bi-card-header">
          <div className="bi-card-title">
            <p>Policy Status</p>
            <p>Active Coverage</p>
          </div>
          <div className="bi-card-icon">🛡️</div>
        </div>
        {[
          { label: "General Liability", limit: "$2M Aggregate", color: "#6366f1" },
          { label: "Cyber & Data Breach", limit: "$5M Aggregate", color: "#0ea5e9" },
          { label: "AI Insurance", limit: "$5M Aggregate", color: "#10b981" }
        ].map((item, i) => (
          <div className="bi-policy-item" key={i}>
            <div className="bi-policy-left">
              <span className="bi-policy-dot" style={{ background: item.color }} />
              {item.label}
            </div>
            <span className="bi-policy-right">{item.limit}</span>
          </div>
        ))}
        <div className="bi-card-cta">
          <button className="bi-card-cta-btn">📄 Generate Instant COI</button>
        </div>
      </motion.div>
    </div>
  </section>
);

const Stats = () => (
  <div className="bi-stats">
    <div className="bi-stats-grid">
      {[
        { num: "30+", label: "Top-rated carrier partners including Hiscox, Chubb, Travelers & more" },
        { num: "10 min", label: "Average time from application to bindable quote" },
        { num: "Instant", label: "Certificate of insurance (COI) generation — no waiting on hold" },
        { num: "50", label: "States covered across all product lines" },
      ].map((s, i) => (
        <motion.div key={i} className="bi-stat-item" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.1 }}>
          <div className="bi-stat-num">{s.num}</div>
          <div className="bi-stat-label">{s.label}</div>
        </motion.div>
      ))}
    </div>
  </div>
);

const ValueProposition = () => (
  <section className="bi-section bi-props">
    <div className="bi-container">
      <div className="bi-section-header">
        <motion.h2 {...fadeIn}>Insurance in 3 simple steps.</motion.h2>
        <motion.p {...fadeIn}>From "tell us what you do" to bindable coverage — no paperwork, no waiting on hold, no weeks of back‑and‑forth with agents.</motion.p>
      </div>
      <div className="bi-props-grid">
        {[
          { emoji: "🎯", color: "amber", title: "Tell us what you do", desc: "Answer a few quick questions about your business, industry, and risk profile. Our platform maps your answers to the right coverage options from 30+ top‑rated carriers — in seconds, not days." },
          { emoji: "📋", color: "indigo", title: "Select your policy", desc: "Browse personalized, bindable quotes side‑by‑side. Compare coverage limits, deductibles, and carrier ratings — then choose the program that fits your budget and compliance requirements." },
          { emoji: "⚡", color: "emerald", title: "Get instant coverage", desc: "Activate your policy immediately and generate certificates of insurance (COIs) in seconds. Share them with clients, landlords, or investors — no calling, emailing, or waiting on hold." }
        ].map((p, i) => (
          <motion.div key={i} className={`bi-prop-card ${p.color}`} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.15 }}>
            <div className={`bi-prop-icon ${p.color}`}>{p.emoji}</div>
            <h3>{p.title}</h3>
            <p>{p.desc}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

interface TailoredVerticalsProps {
  onSelectVertical?: (slug: string) => void;
}

const TailoredVerticals: React.FC<TailoredVerticalsProps> = ({
  onSelectVertical,
}) => (
  <section className="bi-section bi-verticals">
    <div className="bi-container">
      <div className="bi-vert-header">
        <div>
          <motion.h2 {...fadeIn}>Engineered for your industry.</motion.h2>
        </div>
        <motion.p {...fadeIn}>Whether you're compiling code, consulting executives, selling online, or freelancing — we deploy coverage designed for your specific daily exposure.</motion.p>
      </div>
      <div className="bi-vert-grid">
        {[
          {
            emoji: "💻",
            title: "Technology & Software",
            slug: "tech-software",
            desc: "From AI breakthroughs and SaaS platforms to fintech and health‑tech — insurance that scales with you without tripping over regulators, security questionnaires, or IP disputes.",
            tags: ["Tech E&O", "Cyber", "D&O", "AI Insurance"]
          },
          {
            emoji: "🏪",
            title: "E-Commerce & Digital Retail",
            slug: "ecomm-digital",
            desc: "Protect inventory, payments, and brand reputation across DTC, marketplace, and omnichannel operations. From product liability to payment fraud and supply chain coverage.",
            tags: ["Product Liability", "Business Property", "Cyber", "Business Interruption"]
          },
          {
            emoji: "💼",
            title: "Professional Services",
            slug: "professional-services",
            desc: "We protect accountants, consultants, IT firms, and creative agencies so client work doesn't become court work. Safeguard your reputation and fulfill contractual requirements.",
            tags: ["E&O", "General Liability", "Cyber", "Media Liability"]
          },
          {
            emoji: "✏️",
            title: "Freelancers & Solopreneurs",
            slug: "freelancers-solopreneurs",
            desc: "Enterprise-grade protection, simplified and priced for one-person businesses. Meet client contract requirements, generate COIs instantly, and protect your personal assets.",
            tags: ["BOP", "General Liability", "E&O", "Cyber"]
          }
        ].map((v, i) => (
          <motion.div
            key={i}
            className="bi-vert-card"
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: i * 0.1 }}
            onClick={() => onSelectVertical?.(v.slug)}
          >
            <div className="bi-vert-icon">{v.emoji}</div>
            <div>
              <h3>{v.title}</h3>
              <p>{v.desc}</p>
              <div className="bi-tag-list">
                {v.tags.map((tag, tidx) => <span key={tidx} className="bi-tag">{tag}</span>)}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const CoveragePortfolio = ({ onSelectCoverage }: { onSelectCoverage?: (slug: string) => void }) => (
  <section className="bi-section bi-coverage">
    <div className="bi-coverage-glow1" />
    <div className="bi-coverage-glow2" />
    <div className="bi-container" style={{ position: 'relative', zIndex: 1 }}>
      <div className="bi-section-header">
        <motion.h2 {...fadeIn}>A comprehensive defense strategy.</motion.h2>
        <motion.p {...fadeIn}>We analyze the liabilities in your operational footprint and close coverage gaps — from foundational policies every business needs to specialized products for fast‑growing innovators.</motion.p>
      </div>
      <div className="bi-cov-grid">
        {[
          {
            emoji: "🛡️",
            slug: "general-liability",
            name: "General Liability",
            desc: "The bedrock of every business. Protects against third-party bodily injury, property damage, and advertising injury claims. Required by most leases, client contracts, and marketplace onboarding."
          },
          {
            emoji: "📦",
            slug: "bop",
            name: "Business Owner's Policy (BOP)",
            desc: "A smart, cost-efficient bundle combining General Liability and Business Property in one policy. The ideal starting point for small businesses, retailers, restaurants, and lean service teams."
          },
          {
            emoji: "🏥",
            slug: "workers-compensation",
            name: "Workers' Compensation",
            desc: "Required by law in nearly every U.S. state. Covers medical expenses and lost wages for employees injured on the job — protecting your team and shielding the business from workplace liability."
          },
          {
            emoji: "🔒",
            slug: "cyber",
            name: "Cyber & Data Breach",
            desc: "Covers ransomware, data breaches, funds transfer fraud, regulatory investigations, breach notification costs, and forensics. Critical for any business that stores data or operates online."
          },
          {
            emoji: "📋",
            slug: "errors-omissions",
            name: "Errors & Omissions (Tech E&O)",
            desc: "Protects against claims that your services, software, or professional advice caused a client financial loss. Essential for tech companies, consultants, agencies, and all service providers."
          },
          {
            emoji: "👥",
            slug: "directors-officers",
            name: "Directors & Officers (D&O)",
            desc: "Protects personal assets of founders, officers, and board members against investor lawsuits, mismanagement claims, and governance failures. Institutional investors expect D&O by Series A."
          },
          {
            emoji: "⚖️",
            slug: "management-liability",
            name: "Management Liability",
            desc: "A comprehensive bundle covering D&O, Employment Practices Liability, Fiduciary, and Crime — protecting your leadership team, cap table, and the long-term future of your business."
          },
          {
            emoji: "👔",
            slug: "employment-practices",
            name: "Employment Practices Liability",
            desc: "Covers claims from employees or contractors alleging discrimination, wrongful termination, harassment, or wage disputes. Critical protection as you hire, scale, and manage a growing team."
          },
          {
            emoji: "🤖",
            slug: "ai-insurance",
            name: "AI Insurance",
            desc: "Purpose-built coverage for damages caused by AI products and services — including hallucination liability, model bias claims, and downstream decision errors that fall outside traditional Tech E&O scope."
          }
        ].map((pol, i) => (
          <motion.div
            key={i}
            className="bi-cov-card"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.08 }}
            onClick={() => onSelectCoverage?.(pol.slug)}
            style={{ cursor: onSelectCoverage ? 'pointer' : undefined }}
          >
            <span className="bi-cov-icon">{pol.emoji}</span>
            <h4>{pol.name}</h4>
            <p>{pol.desc}</p>
            {onSelectCoverage && (
              <p style={{ marginTop: '14px', fontSize: '12px', color: '#818cf8', fontWeight: 600 }}>Learn more →</p>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const ProactiveRisk = ({ onSelectService }: { onSelectService?: (slug: string) => void }) => (
  <section className="bi-section bi-proactive">
    <div className="bi-container">
      <div className="bi-proactive-inner">
        <motion.div className="bi-proactive-left" initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
          <h2>Complimentary risk services — built in.</h2>
          <p>BizServe goes beyond the policy binder. We offer a suite of complimentary risk services that help you set up coverage correctly, navigate contract requirements, protect your workforce, and advocate for you when claims arise.</p>
          <div className="bi-feature-list">
            {[
              {
                emoji: "📄",
                color: "sky",
                slug: "contract-reviews",
                title: "Contractual Requirements Reviews",
                desc: "Navigating MSA insurance sections, vendor questionnaires, or fundraising requirements? We translate dense contract language into a clear, actionable coverage roadmap — so you never stall a deal over an insurance clause."
              },
              {
                emoji: "📞",
                color: "amber",
                slug: "workforce-risk",
                title: "Workforce Risk Management & Legal Hotline",
                desc: "Growing your team comes with risk. Get immediate advice from top-tier employment counsel on contentious departures, harassment claims, and HR disputes — before they escalate into costly claims."
              },
              {
                emoji: "⚖️",
                color: "emerald",
                slug: "claims",
                title: "Claims Advocacy & Client Support",
                desc: "When incidents happen, you're not fighting the carrier alone. Our claims team advocates on your behalf with integrity and empathy, partners with expert legal counsel, and works to ensure fair, timely resolutions."
              }
            ].map((feat, i) => (
              <motion.div
                key={i}
                className="bi-feature"
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                onClick={() => onSelectService?.(feat.slug)}
                style={{ cursor: onSelectService ? 'pointer' : undefined }}
              >
                <div className={`bi-feature-icon ${feat.color}`}>{feat.emoji}</div>
                <div>
                  <h4>{feat.title}</h4>
                  <p>{feat.desc}</p>
                  {onSelectService && (
                    <p style={{ marginTop: '8px', fontSize: '12px', color: '#0369a1', fontWeight: 600 }}>Learn more →</p>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div className="bi-proactive-right" initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
          <div className="bi-visual-card">
            <div className="bi-visual-dots" />
            <div className="bi-visual-testimonial">
              <span className="bi-visual-dot" />
              <p>"BizServe got us compliant with our enterprise client's insurance requirements in 24 hours — we signed our first $500K contract the very next day. The COI was ready in seconds."</p>
            </div>
            <div className="bi-visual-status">
              <div className="bi-visual-status-header">
                <span>Coverage Status</span>
                <span className="bi-visual-status-badge">Active</span>
              </div>
              <div className="bi-progress-bar">
                <div className="bi-progress-fill" />
              </div>
              <p>COIs generated in under 60 seconds. Most standard property claims reviewed and reimbursed within 2 business days.</p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

const CTA = () => (
  <section className="bi-section bi-cta">
    <div className="bi-container">
      <motion.div className="bi-cta-inner" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
        <div className="bi-cta-glow1" />
        <div className="bi-cta-glow2" />
        <h2>Ready to get covered in minutes?</h2>
        <p>Join businesses that trust BizServe — backed by 30+ top-rated carriers. Get a bindable quote in under 10 minutes, and your certificate of insurance in seconds.</p>
        <div className="bi-cta-btns">
          <button className="bi-cta-primary">Get Your Instant Quote →</button>
          <button className="bi-cta-secondary">Speak to an Advisor</button>
        </div>
      </motion.div>
    </div>
  </section>
);

const OverviewPage = ({
  onSelectVertical,
  onSelectCoverage,
  onSelectService,
}: {
  onSelectVertical?: (slug: string) => void;
  onSelectCoverage?: (slug: string) => void;
  onSelectService?: (slug: string) => void;
}) => (
  <>
    <Hero />
    <Stats />
    <ValueProposition />
    <TailoredVerticals onSelectVertical={onSelectVertical} />
    <CoveragePortfolio onSelectCoverage={onSelectCoverage} />
    <ProactiveRisk onSelectService={onSelectService} />
    <CTA />
  </>
);

const TechSoftwarePage = ({ onBack }: { onBack?: () => void }) => (
  <>
    <Hero
      showBack
      onBack={onBack}
      title="Insurance built for shipping code at scale."
      subtitle="From SaaS platforms and AI products to fintech and health‑tech, BizServe structures coverage that keeps pace with rapid shipping cycles, evolving architectures, demanding security questionnaires, and investor due diligence."
      primaryCta="Design my tech coverage"
      secondaryCta="Review my contracts"
    />
    <section className="bi-industry">
      <div className="bi-industry-inner">
        <div className="bi-industry-header">
          <div>
            <div className="bi-industry-kicker">Who this is for</div>
            <h2 className="bi-industry-title">
              Technology &amp; software teams from seed to scale-up.
            </h2>
            <p className="bi-industry-body">
              Early-stage SaaS, dev tools, AI/ML platforms, fintech, health‑tech,
              cybersecurity, and Web3 companies rely on BizServe to turn complex
              security, legal, and investor expectations into a clear insurance
              architecture that grows with them.
            </p>
          </div>
          <div>
            <p className="bi-industry-body">
              Typical profiles include seed to Series C startups, remote‑first
              engineering teams, usage‑based products, and platforms selling into
              large enterprises with strict vendor requirements.
            </p>
            <div className="bi-pill-list">
              <span className="bi-pill">SaaS &amp; dev tools</span>
              <span className="bi-pill">AI / ML platforms</span>
              <span className="bi-pill">Fintech &amp; Payments</span>
              <span className="bi-pill">Health‑tech</span>
              <span className="bi-pill">Cybersecurity</span>
              <span className="bi-pill">Web3 &amp; Blockchain</span>
              <span className="bi-pill">Hardware &amp; Robotics</span>
              <span className="bi-pill">Remote‑first teams</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--light">
      <div className="bi-industry-inner">
        <h3>Top risks in tech &amp; software</h3>
          <div className="bi-industry-two-col">
            <ul className="bi-risk-list">
              <li>
                <div className="bi-risk-item-title">
                  System outages causing customer financial loss
                </div>
                <div className="bi-risk-item-body">
                  In production this looks like API downtime, failed deployments, or
                  latency spikes during peak usage that force you to issue SLA
                  credits, refunds, or concessions to strategic customers.
                </div>
              </li>
              <li>
                <div className="bi-risk-item-title">
                  Data breach or credential compromise
                </div>
                <div className="bi-risk-item-body">
                  Stolen API keys, compromised admin accounts, or a vendor breach can
                  expose customer data and trigger notification requirements,
                  forensics, and reputational damage.
                </div>
              </li>
              <li>
                <div className="bi-risk-item-title">
                  Regulatory penalties (GDPR, PCI, HIPAA, and beyond)
                </div>
                <div className="bi-risk-item-body">
                  As you grow globally, regulators may investigate how you collect,
                  process, and store personal data, with potential fines and mandated
                  remediation costs.
                </div>
              </li>
            </ul>
            <ul className="bi-risk-list">
              <li>
                <div className="bi-risk-item-title">
                  IP disputes and open‑source license issues
                </div>
                <div className="bi-risk-item-body">
                  Competitors or patent‑holders may claim your product infringes their
                  IP, or OSS components may be used outside their intended license,
                  creating unexpected exposure.
                </div>
              </li>
              <li>
                <div className="bi-risk-item-title">
                  Misrepresentation in sales or marketing
                </div>
                <div className="bi-risk-item-body">
                  Strong claims around uptime, performance, or security posture can be
                  challenged if incidents arise and customers feel the solution was
                  oversold during demos or in marketing collateral.
                </div>
              </li>
              <li>
                <div className="bi-risk-item-title">
                  Leadership &amp; governance risks
                </div>
                <div className="bi-risk-item-body">
                  Investor lawsuits, board disputes, and regulatory investigations can
                  target founders and officers personally — especially as you raise
                  capital and add board members.
                </div>
              </li>
            </ul>
          </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--muted">
      <div className="bi-industry-inner">
        <h3>Recommended coverage stack</h3>
          <div className="bi-coverage-grid">
            <div className="bi-coverage-card">
              <div className="bi-coverage-chip">Core</div>
              <h4>Tech Errors &amp; Omissions</h4>
              <p>
                The backbone for financial‑loss claims when your platform fails,
                underperforms, or misses SLAs. It responds when customers allege your
                software or services caused them to lose revenue.
              </p>
            </div>
            <div className="bi-coverage-card">
              <div className="bi-coverage-chip">Core</div>
              <h4>Cyber Liability</h4>
              <p>
                Covers breach response, forensics, notification, monitoring, and
                certain regulatory investigations when credentials, environments, or
                third‑party vendors are compromised.
              </p>
            </div>
            <div className="bi-coverage-card">
              <div className="bi-coverage-chip">Core</div>
              <h4>General Liability</h4>
              <p>
                Provides foundational protection for premises incidents, some
                advertising injury, and third‑party property damage not tied
                specifically to your software output.
              </p>
            </div>
            <div className="bi-coverage-card">
              <div className="bi-coverage-chip">Leadership</div>
              <h4>Directors &amp; Officers (D&amp;O)</h4>
              <p>
                Shields founders and executives when investors, board members, or
                regulators allege mismanagement, misrepresentation, or governance
                failures during fundraising and growth.
              </p>
            </div>
            <div className="bi-coverage-card">
              <div className="bi-coverage-chip">Optional</div>
              <h4>Media &amp; IP Liability</h4>
              <p>
                Addresses claims tied to your marketing, documentation, and in‑product
                messaging, and disputes around patents or copyrighted material used in
                your product or campaigns.
              </p>
            </div>
            <div className="bi-coverage-card">
              <div className="bi-coverage-chip">AI‑Specific</div>
              <h4>AI Insurance</h4>
              <p>
                Purpose‑built for companies deploying AI products or services. Covers
                damages from model hallucinations, bias claims, and downstream
                decision errors that fall outside traditional Tech E&amp;O scope.
              </p>
            </div>
          </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--light">
      <div className="bi-industry-inner">
        <h3>Coverage by funding stage</h3>
          <div className="bi-industry-two-col">
            <div>
              <p className="bi-industry-body">
                At seed and pre‑seed, the focus is on establishing Tech E&amp;O and
                Cyber with limits that satisfy early adopters and design‑partner
                contracts. As you approach Series A, institutional investors expect
                formal governance and D&amp;O to be in place before they wire funds.
              </p>
            </div>
            <div className="bi-coverage-grid">
              <div className="bi-coverage-card">
                <div className="bi-coverage-chip">Seed</div>
                <p>
                  Right‑sized Tech E&amp;O and Cyber; streamlined documentation to get
                  through initial security reviews without over‑buying coverage.
                </p>
              </div>
              <div className="bi-coverage-card">
                <div className="bi-coverage-chip">Series A</div>
                <p>
                  Higher E&amp;O / Cyber limits, formalized D&amp;O, and policies tuned
                  to enterprise MSAs, vendor portals, and investor requirements.
                </p>
              </div>
              <div className="bi-coverage-card">
                <div className="bi-coverage-chip">Series B+</div>
                <p>
                  Global placements, larger towers, and coverage mapped directly to
                  IPO/exit or strategic acquisition plans.
                </p>
              </div>
            </div>
          </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--muted">
      <div className="bi-industry-inner">
        <h3>Real‑world incident scenarios</h3>
          <div className="bi-scenarios">
            <div className="bi-scenario">
              <div className="bi-scenario-label">Scenario</div>
              <h4>Outage during peak customer traffic</h4>
              <p>
                <strong>What happened:</strong> a deployment causes cascading failures
                across a core API during a customer&apos;s launch window, forcing them
                to delay campaigns and claim lost revenue.
              </p>
              <p>
                <strong>How coverage responds:</strong> Tech E&amp;O can help fund
                legal defense and settlements tied to alleged financial loss.
              </p>
            </div>
            <div className="bi-scenario">
              <div className="bi-scenario-label">Scenario</div>
              <h4>Data breach with regulator involvement</h4>
              <p>
                <strong>What happened:</strong> credentials are compromised via a
                third‑party vendor, exposing PII from multiple regions.
              </p>
              <p>
                <strong>How coverage responds:</strong> Cyber covers forensics,
                notification, credit monitoring, and legal guidance for regulatory
                inquiries where insurable.
              </p>
            </div>
            <div className="bi-scenario">
              <div className="bi-scenario-label">Scenario</div>
              <h4>AI model hallucination</h4>
              <p>
                <strong>What happened:</strong> an AI assistant surfaces inaccurate
                outputs that a customer relies on for a material business decision,
                causing measurable financial harm.
              </p>
              <p>
                <strong>How coverage responds:</strong> AI Insurance and Tech E&amp;O
                can help address alleged damages while we support you in tightening
                contracts, disclosures, and model guardrails.
              </p>
            </div>
          </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--light">
      <div className="bi-industry-inner">
          <h3>Contract requirements &amp; security reviews</h3>
          <div className="bi-industry-two-col">
            <p className="bi-industry-body">
              BizServe helps translate dense MSA insurance sections, vendor
              questionnaires, and portal checklists into a straightforward coverage
              roadmap. We show which policies, limits, and endorsements you actually
              need to close the deal — without over-buying coverage.
            </p>
            <p className="bi-industry-body">
              Instead of juggling legal, security, and sales commentary separately, we
              unify them into a single view of what your enterprise customers and
              investors are asking for — and how your current program measures up.
            </p>
          </div>

          <div className="bi-faq">
            <div className="bi-faq-item">
              <h4>Do I really need D&amp;O before fundraising?</h4>
              <p>
                Yes. Institutional investors increasingly expect D&amp;O to be in place
                before they wire funds. We structure it to align with your board
                composition and cap table without over‑engineering the program.
              </p>
            </div>
            <div className="bi-faq-item">
              <h4>Is cyber coverage separate from Tech E&amp;O?</h4>
              <p>
                Some carriers blend them; others separate them. We help you understand
                how each policy responds so there are no surprises during a breach or
                outage — and ensure your program has no gaps between the two.
              </p>
            </div>
            <div className="bi-faq-item">
              <h4>What limits make sense for a small SaaS with Fortune 500 customers?</h4>
              <p>
                If you sell into large enterprise or public‑sector accounts, we benchmark
                your contracts against peers to recommend realistic, defensible limits
                that still respect your burn rate and stage.
              </p>
            </div>
            <div className="bi-faq-item">
              <h4>Do I need separate AI Insurance on top of Tech E&amp;O?</h4>
              <p>
                For companies where AI outputs drive material decisions or are central
                to the product value proposition, a dedicated AI Insurance policy
                provides clearer, more specific coverage that standard E&amp;O may not
                fully address.
              </p>
            </div>
          </div>
      </div>
    </section>

    <CTA />
  </>
);

const EcommDigitalPage = ({ onBack }: { onBack?: () => void }) => (
  <>
    <Hero
      showBack
      onBack={onBack}
      title="Coverage for carts, clicks, and carriers."
      subtitle="From DTC brands and subscription boxes to marketplace sellers and omnichannel retailers, BizServe protects the inventory, payments, and reputation behind every order — wherever you sell."
      primaryCta="Protect my online store"
      secondaryCta="Audit my product risk"
    />
    <section className="bi-industry">
      <div className="bi-industry-inner">
        <div className="bi-industry-header">
          <div>
            <div className="bi-industry-kicker">Who this is for</div>
            <h2 className="bi-industry-title">
              E‑commerce &amp; digital retail brands selling across channels.
            </h2>
            <p className="bi-industry-body">
              Shopify and WooCommerce brands, marketplace sellers, and
              omnichannel retailers rely on BizServe to keep inventory,
              fulfillment, and payment flows protected as they scale campaigns
              and expand into new channels and geographies.
            </p>
          </div>
          <div>
            <p className="bi-industry-body">
              Typical profiles include DTC brands, marketplace sellers on Amazon,
              Etsy, or Walmart, subscription sellers, retailers with both physical
              locations and online stores, and operators using 3PLs, dropship, or
              private‑label manufacturing.
            </p>
            <div className="bi-pill-list">
              <span className="bi-pill">DTC brands</span>
              <span className="bi-pill">Shopify / WooCommerce</span>
              <span className="bi-pill">Amazon &amp; marketplace sellers</span>
              <span className="bi-pill">Subscription sellers</span>
              <span className="bi-pill">Omnichannel retailers</span>
              <span className="bi-pill">Dropship &amp; private label</span>
              <span className="bi-pill">B2B online sellers</span>
              <span className="bi-pill">International sellers</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--light">
      <div className="bi-industry-inner">
        <h3>Core risks in e‑commerce &amp; digital retail</h3>
        <div className="bi-industry-two-col">
          <ul className="bi-risk-list">
            <li>
              <div className="bi-risk-item-title">Product liability</div>
              <div className="bi-risk-item-body">
                Injuries, allergic reactions, or malfunctioning products that
                cause bodily injury or property damage, whether you fulfill
                orders yourself, through a 3PL, or via dropship arrangements.
                Even if you didn't manufacture the product, you can be held liable as the retailer or importer.
              </div>
            </li>
            <li>
              <div className="bi-risk-item-title">Inventory risk</div>
              <div className="bi-risk-item-body">
                Fire, theft, or water damage at warehouses, micro‑fulfillment
                centers, or third‑party logistics sites that wipes out peak‑
                season stock right when you need it most.
              </div>
            </li>
            <li>
              <div className="bi-risk-item-title">Supply chain disruptions</div>
              <div className="bi-risk-item-body">
                Factory shutdowns, port delays, and carrier issues that leave
                you without product just as campaigns, product launches, or retail
                placements go live.
              </div>
            </li>
          </ul>
          <ul className="bi-risk-list">
            <li>
              <div className="bi-risk-item-title">Chargebacks &amp; payment fraud</div>
              <div className="bi-risk-item-body">
                Card testing, stolen payment methods, and friendly fraud waves
                that erode margins and disrupt your payment processor
                relationships at the worst possible times.
              </div>
            </li>
            <li>
              <div className="bi-risk-item-title">Brand and advertising risk</div>
              <div className="bi-risk-item-body">
                Claims tied to user‑generated content, influencer campaigns,
                comparative ads, or product claims made across social, email,
                and marketplace listings.
              </div>
            </li>
            <li>
              <div className="bi-risk-item-title">Business interruption</div>
              <div className="bi-risk-item-body">
                A warehouse fire, major equipment failure, or cyber incident during peak selling season can halt operations and destroy months of revenue — without a financial safety net in place.
              </div>
            </li>
          </ul>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--muted">
      <div className="bi-industry-inner">
        <h3>Recommended coverage stack</h3>
        <div className="bi-coverage-grid">
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Core</div>
            <h4>Product Liability</h4>
            <p>
              Covers bodily injury or property damage caused by products you
              manufacture, import, or sell online or in‑store, including
              private‑label and white‑label goods. Required by most major retailers and marketplaces.
            </p>
          </div>
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Core</div>
            <h4>Business Property / Stock Throughput</h4>
            <p>
              Protects inventory and business property at owned, leased, and
              third‑party locations, as well as goods in transit between
              factories, warehouses, and customers.
            </p>
          </div>
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Core</div>
            <h4>Business Interruption</h4>
            <p>
              Replaces lost income when a covered event, like a warehouse fire
              or major equipment failure, forces operations to pause during key
              selling windows — protecting your revenue when it matters most.
            </p>
          </div>
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Digital</div>
            <h4>Cyber &amp; Payment Fraud</h4>
            <p>
              Addresses data breaches, compromised checkout forms, and certain
              fraud events impacting payment flows, customer PII, and platform
              trust. Essential for all online retailers handling payment data.
            </p>
          </div>
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Core</div>
            <h4>General Liability</h4>
            <p>
              Baseline protection for premises incidents, shopper injuries in
              showrooms or pop‑ups, and some advertising injury tied to your
              brand campaigns and marketplace listings.
            </p>
          </div>
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Optional</div>
            <h4>Trade Shows, Inland Marine &amp; Recalls</h4>
            <p>
              Tailors your program for live events, goods in transit, and
              large‑scale recalls initiated by regulators, retailers, or your
              own quality controls — protecting your brand at every touchpoint.
            </p>
          </div>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--light">
      <div className="bi-industry-inner">
        <h3>Retailer &amp; marketplace requirements</h3>
        <div className="bi-industry-two-col">
          <div>
            <p className="bi-industry-body">
              Selling through Amazon, Walmart, Target, Costco, or big‑box retailers means
              navigating strict insurance language. Buyers expect clear evidence
              of product liability limits, additional insured status, and
              primary, non‑contributory wording on your certificates.
            </p>
          </div>
          <div>
            <p className="bi-industry-body">
              BizServe maps the requirements from vendor manuals and retail
              portals into a simple checklist, so you know exactly which
              policies, limits, and endorsements you need to keep shelves
              stocked and storefronts live — without delays or surprises.
            </p>
          </div>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--muted">
      <div className="bi-industry-inner">
        <h3>Real‑world incident scenarios</h3>
        <div className="bi-scenarios">
          <div className="bi-scenario">
            <div className="bi-scenario-label">Scenario</div>
            <h4>Product recall during peak season</h4>
            <p>
              <strong>What happened:</strong> a safety issue forces you to pull
              an entire batch from shelves and marketplaces just as holiday
              campaigns launch.
            </p>
            <p>
              <strong>How coverage responds:</strong> recall and property
              coverage can help fund testing, notifications, reverse logistics,
              and replacement stock where available.
            </p>
          </div>
          <div className="bi-scenario">
            <div className="bi-scenario-label">Scenario</div>
            <h4>Warehouse fire impacts inventory</h4>
            <p>
              <strong>What happened:</strong> a fire at a 3PL facility destroys
              your best‑selling SKUs weeks before a major campaign.
            </p>
            <p>
              <strong>How coverage responds:</strong> stock throughput and
              business interruption coverage work together to replace inventory
              and recover lost income where covered.
            </p>
          </div>
          <div className="bi-scenario">
            <div className="bi-scenario-label">Scenario</div>
            <h4>Fraud wave from compromised checkout</h4>
            <p>
              <strong>What happened:</strong> attackers abuse a checkout form
              for card testing, triggering chargebacks and payment processor scrutiny.
            </p>
            <p>
              <strong>How coverage responds:</strong> cyber coverage can help
              fund forensics, notifications, and response, while shaping better
              controls to reduce future fraud exposure.
            </p>
          </div>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--light">
      <div className="bi-industry-inner">
        <h3>Frequently asked questions</h3>
        <div className="bi-faq">
          <div className="bi-faq-item">
            <h4>Do I need product liability if I only drop‑ship?</h4>
            <p>
              Yes. Even if you don't manufacture or warehouse the product, you can be held
              liable as the retailer or importer. Product liability is a core coverage for
              all drop‑shippers — and most marketplaces require it.
            </p>
          </div>
          <div className="bi-faq-item">
            <h4>Will Amazon's own insurance policy protect me?</h4>
            <p>
              Amazon's policy protects Amazon — not you. Marketplace sellers are
              often required to carry their own product liability insurance to
              maintain selling privileges and protect themselves from claims.
            </p>
          </div>
          <div className="bi-faq-item">
            <h4>What if my 3PL loses or damages my inventory?</h4>
            <p>
              Your 3PL may have limited liability under their standard contract terms.
              Stock throughput and business property coverage can fill the gap and
              protect your inventory at third-party locations.
            </p>
          </div>
          <div className="bi-faq-item">
            <h4>How do I prove loss during a major campaign?</h4>
            <p>
              Good documentation practices — sales records, inventory logs, and
              marketing spend reports — are key. We can help you understand what
              documentation carriers will expect when you file a claim.
            </p>
          </div>
        </div>
      </div>
    </section>

    <CTA />
  </>
);

const ProfessionalServicesPage = ({ onBack }: { onBack?: () => void }) => (
  <>
    <Hero
      showBack
      onBack={onBack}
      title="Insurance that keeps client work from becoming court work."
      subtitle="For consultants, agencies, accountants, IT firms, and advisors, BizServe turns complex 'bad advice' and missed‑deadline exposures into a clear professional liability strategy — so you stay focused on delivering great work."
      primaryCta="Protect my service practice"
      secondaryCta="Review my client contracts"
    />
    <section className="bi-industry">
      <div className="bi-industry-inner">
        <div className="bi-industry-header">
          <div>
            <div className="bi-industry-kicker">Who this is for</div>
            <h2 className="bi-industry-title">
              Professional service firms built on expertise and trust.
            </h2>
            <p className="bi-industry-body">
              Management consultants, marketing and creative agencies,
              accountants and bookkeepers, IT services firms, HR advisors,
              recruiters, architects, and fractional executives all depend on
              client confidence in their recommendations and deliverables.
            </p>
          </div>
          <div>
            <p className="bi-industry-body">
              Whether you run a boutique firm or a growing agency network, we
              design coverage that matches the way you scope work, manage
              subcontractors, and handle sensitive client data.
            </p>
            <div className="bi-pill-list">
              <span className="bi-pill">Consulting firms</span>
              <span className="bi-pill">Agencies &amp; studios</span>
              <span className="bi-pill">Accountants &amp; bookkeepers</span>
              <span className="bi-pill">IT services firms</span>
              <span className="bi-pill">Architecture &amp; engineering</span>
              <span className="bi-pill">HR &amp; recruiting shops</span>
              <span className="bi-pill">Fractional executives</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--light">
      <div className="bi-industry-inner">
        <h3>Typical exposures in professional services</h3>
        <div className="bi-industry-two-col">
          <ul className="bi-risk-list">
            <li>
              <div className="bi-risk-item-title">
                Professional mistakes or omissions
              </div>
              <div className="bi-risk-item-body">
                Advice, analysis, or deliverables that a client claims were
                wrong, incomplete, or not fit for purpose, leading to measurable
                financial loss.
              </div>
            </li>
            <li>
              <div className="bi-risk-item-title">
                Missed deadlines and project slippage
              </div>
              <div className="bi-risk-item-body">
                Delays that cause launches, campaigns, or filings to miss
                critical dates, triggering demands for refunds, credits, or
                damages from clients.
              </div>
            </li>
            <li>
              <div className="bi-risk-item-title">
                Miscommunication on scope or deliverables
              </div>
              <div className="bi-risk-item-body">
                Ambiguous SOWs and change orders that leave room for dispute
                over what was promised, what was delivered, and who owns the
                resulting IP.
              </div>
            </li>
          </ul>
          <ul className="bi-risk-list">
            <li>
              <div className="bi-risk-item-title">Misuse of client data</div>
              <div className="bi-risk-item-body">
                Handling sensitive financial, HR, or marketing data without
                proper controls, leading to unauthorized access or disclosure
                that triggers regulatory and reputational exposure.
              </div>
            </li>
            <li>
              <div className="bi-risk-item-title">Intellectual property issues</div>
              <div className="bi-risk-item-body">
                Using unlicensed assets, reusing templates without rights, or
                disputes over who owns creative and strategic work product
                created under your engagements.
              </div>
            </li>
            <li>
              <div className="bi-risk-item-title">Contract friction &amp; scope creep</div>
              <div className="bi-risk-item-body">
                Poorly written indemnity clauses and limitation-of-liability sections
                can shift disproportionate risk onto your firm when a project goes
                sideways or a client relationship sours.
              </div>
            </li>
          </ul>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--muted">
      <div className="bi-industry-inner">
        <h3>Recommended coverage stack</h3>
        <div className="bi-coverage-grid">
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Core</div>
            <h4>Professional Liability (E&amp;O)</h4>
            <p>
              The centerpiece for service businesses. Responds when clients
              allege your recommendations, analysis, or execution caused them
              financial harm — regardless of whether the claim has merit.
            </p>
          </div>
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Core</div>
            <h4>General Liability</h4>
            <p>
              Covers premises‑based incidents, third‑party property damage, and
              some advertising injury arising from your operations and client
              interactions.
            </p>
          </div>
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Data</div>
            <h4>Cyber Liability</h4>
            <p>
              Protects against email compromise, ransomware, and breaches when
              you access or store client data, including through SaaS tools,
              cloud platforms, and third-party vendors.
            </p>
          </div>
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Media</div>
            <h4>Media &amp; Advertising Liability</h4>
            <p>
              Critical for agencies and creative shops producing campaigns,
              content, or assets that could trigger defamation, copyright, or
              trademark disputes with clients or third parties.
            </p>
          </div>
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">People</div>
            <h4>Employment Practices Liability</h4>
            <p>
              Helps protect against certain claims from employees or
              contractors, especially as you scale teams, hire globally, and
              manage complex employment relationships.
            </p>
          </div>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--light">
      <div className="bi-industry-inner">
        <h3>Engagements, SOWs &amp; client contracts</h3>
        <div className="bi-industry-two-col">
          <div>
            <p className="bi-industry-body">
              Poorly written SOWs, indemnity clauses, and limitation‑of‑liability
              sections can shift disproportionate risk onto your firm. We help
              you translate dense contract language into practical guardrails
              you can negotiate before the project starts.
            </p>
          </div>
          <div>
            <p className="bi-industry-body">
              BizServe reviews insurance requirements, scopes of work, and
              sub‑contracting language so your policies and contracts work
              together, not against each other, when a project goes sideways
              or a client relationship ends badly.
            </p>
          </div>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--muted">
      <div className="bi-industry-inner">
        <h3>Real‑world examples</h3>
        <div className="bi-scenarios">
          <div className="bi-scenario">
            <div className="bi-scenario-label">Scenario</div>
            <h4>Strategy recommendation drives a failed launch</h4>
            <p>
              <strong>What happened:</strong> a consultant&apos;s go‑to‑market
              plan underestimates budget and timing, contributing to a costly,
              underperforming launch that the client blames on the firm.
            </p>
            <p>
              <strong>How coverage responds:</strong> professional liability
              helps fund legal defense and, where covered, settlements tied to
              alleged negligence.
            </p>
          </div>
          <div className="bi-scenario">
            <div className="bi-scenario-label">Scenario</div>
            <h4>Accounting error triggers penalties</h4>
            <p>
              <strong>What happened:</strong> a bookkeeping oversight leads to
              missed tax payments and regulatory fines for the client.
            </p>
            <p>
              <strong>How coverage responds:</strong> E&amp;O coverage can step
              in for defense costs and certain damages, while improved QA
              controls reduce recurrence.
            </p>
          </div>
          <div className="bi-scenario">
            <div className="bi-scenario-label">Scenario</div>
            <h4>Creative work sparks an IP dispute</h4>
            <p>
              <strong>What happened:</strong> an agency asset allegedly uses an
              unlicensed image in a global campaign, prompting a copyright
              claim.
            </p>
            <p>
              <strong>How coverage responds:</strong> media and E&amp;O
              coverage can help manage defense and settlement, while updated
              asset‑rights workflows lower future risk.
            </p>
          </div>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--light">
      <div className="bi-industry-inner">
        <h3>Risk reduction &amp; frequently asked questions</h3>
        <div className="bi-industry-two-col">
          <div>
            <p className="bi-industry-body">
              Standardized contracts, disciplined change‑order processes, and QA
              checklists before go‑lives dramatically reduce claim frequency and
              support better pricing from underwriters over time.
            </p>
          </div>
          <div>
            <p className="bi-industry-body">
              Good documentation — signed SOWs, approved change orders, and
              delivery confirmations — is your first line of defense if a client
              relationship turns adversarial.
            </p>
          </div>
        </div>
        <div className="bi-faq">
          <div className="bi-faq-item">
            <h4>Is E&amp;O different from general liability?</h4>
            <p>
              Yes. General liability covers physical incidents (bodily injury, property damage).
              E&amp;O covers financial loss claims arising from your professional advice,
              work product, or service delivery. Most professional service firms need both.
            </p>
          </div>
          <div className="bi-faq-item">
            <h4>Do I need insurance if I'm a one-person consultancy?</h4>
            <p>
              Absolutely. Even solo practitioners can face significant claims. Many enterprise
              clients require proof of E&amp;O and General Liability before onboarding a
              contractor — and your personal assets may be at risk without coverage.
            </p>
          </div>
          <div className="bi-faq-item">
            <h4>Will my client's contract force me to increase limits?</h4>
            <p>
              Likely yes as you land larger clients. Enterprise contracts often specify
              minimum coverage limits. BizServe helps you benchmark your program against
              contract requirements and scale coverage as your client base grows.
            </p>
          </div>
          <div className="bi-faq-item">
            <h4>What if I subcontract part of the work?</h4>
            <p>
              You may still be liable for the subcontractor's work in the client's eyes.
              We help structure your policies and subcontractor agreements to ensure
              coverage flows correctly and gaps are closed.
            </p>
          </div>
        </div>
      </div>
    </section>

    <CTA />
  </>
);

const FreelancersSolopreneursPage = ({ onBack }: { onBack?: () => void }) => (
  <>
    <Hero
      showBack
      onBack={onBack}
      title="Big‑company coverage for one‑person powerhouses."
      subtitle="Independent designers, developers, consultants, and coaches use BizServe to get enterprise‑grade protection, simplified and priced for solo businesses. Get client‑ready in minutes."
      primaryCta="Get client‑ready in minutes"
      secondaryCta="Generate my proof of insurance"
    />
    <section className="bi-industry">
      <div className="bi-industry-inner">
        <div className="bi-industry-header">
          <div>
            <div className="bi-industry-kicker">Who this is for</div>
            <h2 className="bi-industry-title">
              Freelancers and solopreneurs working with serious clients.
            </h2>
            <p className="bi-industry-body">
              From independent developers and designers to strategy consultants,
              coaches, and fractional CxOs, BizServe gives you the coverage
              large clients expect without slowing down your pipeline or
              eroding your margins.
            </p>
          </div>
          <div>
            <p className="bi-industry-body">
              Ideal for one‑person LLCs, sole props, and independent contractors
              who need certificates of insurance to onboard, get paid, and
              protect personal assets from business claims.
            </p>
            <div className="bi-pill-list">
              <span className="bi-pill">Web &amp; app designers</span>
              <span className="bi-pill">Software developers</span>
              <span className="bi-pill">UI / UX designers</span>
              <span className="bi-pill">Graphic designers &amp; animators</span>
              <span className="bi-pill">Digital marketers</span>
              <span className="bi-pill">Social media managers</span>
              <span className="bi-pill">Photographers &amp; videographers</span>
              <span className="bi-pill">Writers &amp; translators</span>
              <span className="bi-pill">Independent consultants</span>
              <span className="bi-pill">Coaches &amp; trainers</span>
              <span className="bi-pill">Fractional leaders</span>
              <span className="bi-pill">Event planners</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--light">
      <div className="bi-industry-inner">
        <h3>Why coverage matters even if you&apos;re small</h3>
        <div className="bi-industry-two-col">
          <div>
            <p className="bi-industry-body">
              Enterprise and mid‑market clients are steadily shifting more risk
              to contractors. Insurance requirements in their vendor packets are
              no longer optional if you want to keep or grow the relationship —
              and marketplaces like Fiverr have begun requiring proof of coverage too.
            </p>
          </div>
          <div>
            <p className="bi-industry-body">
              Certificates of insurance are now table stakes to get onboarded,
              access systems, and receive payment. With personal assets on the
              line for many freelancers operating as sole props or single-member LLCs,
              the right policy design truly matters.
            </p>
          </div>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--muted">
      <div className="bi-industry-inner">
        <h3>Typical contract requirements</h3>
        <div className="bi-industry-two-col">
          <div>
            <p className="bi-industry-body">
              We see recurring asks for general liability with specific limits,
              professional liability for your advice or deliverables, and cyber
              coverage whenever you handle sensitive or regulated data on behalf
              of a client.
            </p>
          </div>
          <div>
            <p className="bi-industry-body">
              BizServe explains concepts like additional insured, waiver of
              subrogation, and primary/non‑contributory wording in plain
              English, then configures endorsements so your COIs match exactly
              what contracts call for — no guesswork.
            </p>
          </div>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--light">
      <div className="bi-industry-inner">
        <h3>Smart, minimal coverage stack</h3>
        <div className="bi-coverage-grid">
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Core</div>
            <h4>BOP (Business Owner&apos;s Policy)</h4>
            <p>
              A lean bundle that combines general liability with property
              coverage for essential gear, giving solos a solid base at an
              efficient price point — ideal for home-office and coworking setups.
            </p>
          </div>
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Core</div>
            <h4>Professional Liability</h4>
            <p>
              Addresses claims that your advice, creative work, or project
              execution caused a client financial loss, even when you&apos;re
              just one person. Required by most enterprise clients.
            </p>
          </div>
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Data</div>
            <h4>Cyber</h4>
            <p>
              Helps when laptops with client data are stolen, inboxes are
              compromised, or synced cloud tools expose information you&apos;re
              responsible for under your client agreements.
            </p>
          </div>
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Optional</div>
            <h4>Equipment &amp; Non‑Owned Auto</h4>
            <p>
              Optional add‑ons for high‑value equipment and certain auto
              exposures when you rely on rideshare, rentals, or client travel to
              deliver work on-site.
            </p>
          </div>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--muted">
      <div className="bi-industry-inner">
        <h3>Income protection &amp; continuity</h3>
        <div className="bi-industry-two-col">
          <div>
            <p className="bi-industry-body">
              Even a modest claim can disrupt your pipeline, reputation, and
              cash flow. We highlight options that help you weather business
              interruption from covered events and, where in scope, certain
              income‑loss scenarios.
            </p>
          </div>
          <div>
            <p className="bi-industry-body">
              The goal is simple: make sure an incident with one client doesn&apos;t
              end your independent career or derail your long‑term plans. One
              uninsured lawsuit could wipe out years of savings.
            </p>
          </div>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--light">
      <div className="bi-industry-inner">
        <h3>Practical scenarios</h3>
        <div className="bi-scenarios">
          <div className="bi-scenario">
            <div className="bi-scenario-label">Scenario</div>
            <h4>Missed deadline on a key campaign</h4>
            <p>
              <strong>What happened:</strong> a solo creative misses a critical
              launch window, and the client alleges revenue loss tied to the
              delay.
            </p>
            <p>
              <strong>How coverage responds:</strong> professional liability can
              help with defense and certain damages while better scoping and
              change‑order habits reduce future risk.
            </p>
          </div>
          <div className="bi-scenario">
            <div className="bi-scenario-label">Scenario</div>
            <h4>Laptop with client data stolen</h4>
            <p>
              <strong>What happened:</strong> a device with synced client files
              is taken from a café, raising concerns about data exposure and
              client notification obligations.
            </p>
            <p>
              <strong>How coverage responds:</strong> cyber coverage can support
              notifications, forensics, and PR guidance while equipment
              coverage helps replace critical tools.
            </p>
          </div>
          <div className="bi-scenario">
            <div className="bi-scenario-label">Scenario</div>
            <h4>Client alleges plagiarism or IP infringement</h4>
            <p>
              <strong>What happened:</strong> a client claims your work
              infringes another party&apos;s IP or wasn&apos;t properly licensed,
              demanding compensation.
            </p>
            <p>
              <strong>How coverage responds:</strong> E&amp;O and media
              extensions can help handle defense and negotiation while we adjust
              your workflows to avoid repeat issues.
            </p>
          </div>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--muted">
      <div className="bi-industry-inner">
        <h3>Frequently asked questions</h3>
        <div className="bi-faq">
          <div className="bi-faq-item">
            <h4>Can I pass the cost of insurance through to my clients?</h4>
            <p>
              Many freelancers do factor insurance premiums into their rates or bill
              it as a line item. How you handle it depends on your contract structure —
              we can help you think through the best approach for your practice.
            </p>
          </div>
          <div className="bi-faq-item">
            <h4>Do I need separate coverage for each client or engagement?</h4>
            <p>
              Generally no — one policy covers all your engagements. However, if a specific
              client requires you to be the named insured on a dedicated policy, we can
              structure that arrangement as well.
            </p>
          </div>
          <div className="bi-faq-item">
            <h4>What if I work out of a coworking space or from home?</h4>
            <p>
              Coverage can be structured to protect your home-office setup or work across
              coworking locations. A BOP typically covers your equipment and provides general
              liability regardless of where you work from.
            </p>
          </div>
          <div className="bi-faq-item">
            <h4>How quickly can I get a certificate of insurance?</h4>
            <p>
              Once your policy is active, COIs are generated instantly — in seconds, not days.
              BizServe is built to answer "Can I get this certificate by tomorrow?" with a
              simple yes, so you can sign the SOW and start the work.
            </p>
          </div>
        </div>
      </div>
    </section>

    <CTA />
  </>
);

/* ─────────────────────────────────────────────────────────────
   COVERAGE PAGES (9)
───────────────────────────────────────────────────────────── */

const GeneralLiabilityPage = ({ onBack }: { onBack?: () => void }) => (
  <>
    <Hero
      showBack
      onBack={onBack}
      title="The foundation every business needs."
      subtitle="Commercial General Liability (CGL) is the most common business policy — and the one required by nearly every lease, client contract, and marketplace onboarding process."
      primaryCta="Get a GL quote"
      secondaryCta="Review my contracts"
    />
    <section className="bi-industry-section bi-industry-section--light">
      <div className="bi-industry-inner">
        <h3>What general liability covers</h3>
        <div className="bi-coverage-grid">
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Bodily Injury</div>
            <h4>Third-Party Injuries</h4>
            <p>If a customer or other third party is injured on your premises or as a result of your operations, BizServe can cover medical expenses, legal costs, and damages.</p>
          </div>
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Property Damage</div>
            <h4>Third-Party Property</h4>
            <p>If your business accidentally damages someone else's property, you may be required to cover the cost of repair or replacement. GL responds to those claims.</p>
          </div>
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Advertising Injury</div>
            <h4>Personal &amp; Advertising Injury</h4>
            <p>If a third party claims your business damaged their reputation or plagiarized their intellectual property, GL covers legal defense and associated expenses — regardless of whether the claim has merit.</p>
          </div>
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Products</div>
            <h4>Product Liability</h4>
            <p>Products developed or sold by your business that harm people or property can trigger legal and medical costs. GL's product liability component helps cover those exposures.</p>
          </div>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--muted">
      <div className="bi-industry-inner">
        <h3>What general liability does NOT cover</h3>
        <div className="bi-industry-two-col">
          <ul className="bi-risk-list">
            <li>
              <div className="bi-risk-item-title">Cyber incidents &amp; data breaches</div>
              <div className="bi-risk-item-body">Digital risks require a dedicated Cyber policy. GL does not respond to unauthorized data access, ransomware, or breach notification costs.</div>
            </li>
            <li>
              <div className="bi-risk-item-title">Professional mistakes &amp; advice</div>
              <div className="bi-risk-item-body">Errors in your services, consulting, or software require Errors &amp; Omissions (E&amp;O) coverage. GL only covers physical harm, not financial losses from professional work.</div>
            </li>
            <li>
              <div className="bi-risk-item-title">Employee injuries</div>
              <div className="bi-risk-item-body">Workplace injuries to your own employees are covered under Workers' Compensation — not GL.</div>
            </li>
          </ul>
          <ul className="bi-risk-list">
            <li>
              <div className="bi-risk-item-title">Damage to your own property</div>
              <div className="bi-risk-item-body">GL does not cover damage to your own business premises or equipment. A Business Owner's Policy (BOP) bundles GL with Business Property coverage.</div>
            </li>
            <li>
              <div className="bi-risk-item-title">Auto accidents</div>
              <div className="bi-risk-item-body">Vehicle-related incidents — even for business use — require commercial auto or hired and non-owned auto coverage.</div>
            </li>
            <li>
              <div className="bi-risk-item-title">Intentional acts</div>
              <div className="bi-risk-item-body">Deliberate harm or fraud committed by you or your employees is excluded from GL coverage.</div>
            </li>
          </ul>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--light">
      <div className="bi-industry-inner">
        <h3>Who needs general liability insurance?</h3>
        <ul className="bi-risk-list">
          <li>
            <div className="bi-risk-item-title">Small businesses of every kind</div>
            <div className="bi-risk-item-body">Retailers, restaurants, laundromats, hotels, auto services, real estate agencies — any business with customers, a physical location, or products needs GL protection.</div>
          </li>
          <li>
            <div className="bi-risk-item-title">Startups and tech companies</div>
            <div className="bi-risk-item-body">Investors and enterprise clients frequently require proof of GL insurance as part of diligence, vendor onboarding, and commercial agreements.</div>
          </li>
          <li>
            <div className="bi-risk-item-title">Freelancers and service providers</div>
            <div className="bi-risk-item-body">Marketplace platforms like Fiverr, contractors working on client sites, and independent professionals are often contractually required to carry GL.</div>
          </li>
          <li>
            <div className="bi-risk-item-title">E-commerce merchants</div>
            <div className="bi-risk-item-body">Selling online doesn't eliminate physical risk — product defects, advertising injury, and third-party property damage still apply to online sellers.</div>
          </li>
        </ul>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--muted">
      <div className="bi-industry-inner">
        <h3>How much does general liability cost?</h3>
        <ul className="bi-risk-list">
          <li>
            <div className="bi-risk-item-title">Your business location</div>
            <div className="bi-risk-item-body">State regulations and local risk environments affect premium pricing.</div>
          </li>
          <li>
            <div className="bi-risk-item-title">Number of employees</div>
            <div className="bi-risk-item-body">More employees generally means more exposure and a higher premium.</div>
          </li>
          <li>
            <div className="bi-risk-item-title">Revenue and payroll</div>
            <div className="bi-risk-item-body">Higher revenue and payroll typically correlates with greater business activity and risk.</div>
          </li>
          <li>
            <div className="bi-risk-item-title">Industry and operations type</div>
            <div className="bi-risk-item-body">The type of business you operate — and the products or services you provide — is one of the biggest pricing factors.</div>
          </li>
        </ul>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--light">
      <div className="bi-industry-inner">
        <h3>Frequently asked questions</h3>
        <div className="bi-faq">
          <div className="bi-faq-item">
            <h4>Is general liability insurance required by law?</h4>
            <p>Requirements vary by state and industry. However, your business can be exposed to high financial risk without it. Many clients, leases, and marketplace platforms require proof of GL before you can do business with them.</p>
          </div>
          <div className="bi-faq-item">
            <h4>How do I show proof of general liability coverage?</h4>
            <p>A Certificate of Insurance (COI) serves as your proof of coverage and can be sent instantly to any client, landlord, or marketplace that requires it. BizServe generates COIs in seconds.</p>
          </div>
          <div className="bi-faq-item">
            <h4>What is an additional insured?</h4>
            <p>An additional insured is any third party — such as a vendor, landlord, or client — that your policy extends coverage to. Many enterprise contracts and commercial leases require this.</p>
          </div>
          <div className="bi-faq-item">
            <h4>Does general liability cover my office or physical location?</h4>
            <p>Damage to your own business location is not covered under a standard GL policy. For that protection, you need a Business Owner's Policy (BOP), which bundles GL with Business Property coverage.</p>
          </div>
        </div>
      </div>
    </section>

    <CTA />
  </>
);

const BopPage = ({ onBack }: { onBack?: () => void }) => (
  <>
    <Hero
      showBack
      onBack={onBack}
      title="Two essential coverages. One efficient policy."
      subtitle="A Business Owner's Policy (BOP) bundles General Liability and Business Property insurance into one cost-efficient package — the smart starting point for small businesses, retailers, restaurants, and lean service teams."
      primaryCta="Get a BOP quote"
      secondaryCta="Compare coverage options"
    />
    <section className="bi-industry-section bi-industry-section--light">
      <div className="bi-industry-inner">
        <h3>What a BOP bundles</h3>
        <div className="bi-industry-two-col">
          <div>
            <h4 style={{ fontWeight: 700, color: '#0f172a', marginBottom: '10px' }}>General Liability</h4>
            <p className="bi-industry-body">Protects against third-party bodily injury, property damage, and personal or advertising injury claims. Required by most commercial leases, client contracts, and marketplace onboarding processes.</p>
            <ul className="bi-risk-list" style={{ marginTop: '14px' }}>
              <li><div className="bi-risk-item-title">Bodily injury to third parties</div></li>
              <li><div className="bi-risk-item-title">Third-party property damage</div></li>
              <li><div className="bi-risk-item-title">Advertising &amp; reputational injury</div></li>
              <li><div className="bi-risk-item-title">Product liability</div></li>
            </ul>
          </div>
          <div>
            <h4 style={{ fontWeight: 700, color: '#0f172a', marginBottom: '10px' }}>Business Property</h4>
            <p className="bi-industry-body">Protects your business's physical assets — equipment, inventory, furniture, and the building itself — from disaster and theft. Covers both your owned property and property of others in your care.</p>
            <ul className="bi-risk-list" style={{ marginTop: '14px' }}>
              <li><div className="bi-risk-item-title">Equipment and computers</div></li>
              <li><div className="bi-risk-item-title">Inventory and merchandise</div></li>
              <li><div className="bi-risk-item-title">Fire, theft, and vandalism</div></li>
              <li><div className="bi-risk-item-title">Business interruption (in some forms)</div></li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--muted">
      <div className="bi-industry-inner">
        <h3>Who a BOP is ideal for</h3>
        <ul className="bi-risk-list">
          <li>
            <div className="bi-risk-item-title">Small businesses and main street retailers</div>
            <div className="bi-risk-item-body">Shops, boutiques, and service businesses with a physical location need both liability and property protection — and BOP delivers both efficiently.</div>
          </li>
          <li>
            <div className="bi-risk-item-title">Restaurants and food service businesses</div>
            <div className="bi-risk-item-body">Between customer injuries, equipment, inventory, and third-party property, restaurants need both GL and Property — BOP bundles them cost-effectively.</div>
          </li>
          <li>
            <div className="bi-risk-item-title">Freelancers and home-office solopreneurs</div>
            <div className="bi-risk-item-body">BOP can be structured to protect home-office equipment and provide general liability regardless of where you work from — ideal for lean operations.</div>
          </li>
          <li>
            <div className="bi-risk-item-title">Service teams with physical operations</div>
            <div className="bi-risk-item-body">Cleaning services, contractors, installers, and field teams working in client spaces benefit from bundled coverage against both property and liability exposures.</div>
          </li>
        </ul>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--light">
      <div className="bi-industry-inner">
        <h3>BOP vs. buying separately</h3>
        <div className="bi-industry-two-col">
          <div>
            <h4 style={{ fontWeight: 700, color: '#0f172a', marginBottom: '10px' }}>The cost advantage</h4>
            <p className="bi-industry-body">Bundling GL and Business Property in a BOP is typically more cost-efficient than purchasing two standalone policies. Carriers price the bundle favorably because the risks are correlated and managed together.</p>
          </div>
          <div>
            <h4 style={{ fontWeight: 700, color: '#0f172a', marginBottom: '10px' }}>Simplified management</h4>
            <p className="bi-industry-body">One policy, one renewal date, one certificate of insurance. Managing a BOP simplifies your insurance program, reduces administrative overhead, and makes it easier to demonstrate coverage to clients and landlords.</p>
          </div>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--muted">
      <div className="bi-industry-inner">
        <h3>What BOP does NOT cover</h3>
        <div className="bi-coverage-grid">
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Separate Policy</div>
            <h4>Cyber &amp; Data Breach</h4>
            <p>Digital risks — ransomware, data breaches, business interruption from cyberattacks — require a dedicated Cyber policy. BOP does not cover these exposures.</p>
          </div>
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Separate Policy</div>
            <h4>Professional Liability (E&amp;O)</h4>
            <p>Claims arising from your professional services, advice, or software require an Errors &amp; Omissions policy. BOP covers physical harm, not service errors.</p>
          </div>
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Separate Policy</div>
            <h4>Workers' Compensation</h4>
            <p>Employee injuries and illness sustained on the job require a Workers' Compensation policy — legally required in nearly every U.S. state.</p>
          </div>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--light">
      <div className="bi-industry-inner">
        <h3>Frequently asked questions</h3>
        <div className="bi-faq">
          <div className="bi-faq-item">
            <h4>Is a BOP the same as General Liability?</h4>
            <p>No — a BOP includes General Liability plus Business Property coverage. GL alone only covers third-party claims; BOP adds protection for your own physical assets.</p>
          </div>
          <div className="bi-faq-item">
            <h4>Can freelancers get a BOP?</h4>
            <p>Yes. Even without a traditional office, a BOP can cover your home-office equipment and provide the GL coverage that clients and marketplaces require.</p>
          </div>
          <div className="bi-faq-item">
            <h4>Does BOP include business interruption coverage?</h4>
            <p>Many BOP forms include business interruption as part of the property component, covering lost income when a covered event forces you to temporarily close.</p>
          </div>
          <div className="bi-faq-item">
            <h4>How do I know if I need a BOP or just GL?</h4>
            <p>If you have physical business property worth protecting — equipment, inventory, furniture — a BOP makes sense. If you work entirely remotely with no significant physical assets, standalone GL may be sufficient.</p>
          </div>
        </div>
      </div>
    </section>

    <CTA />
  </>
);

const WorkersCompPage = ({ onBack }: { onBack?: () => void }) => (
  <>
    <Hero
      showBack
      onBack={onBack}
      title="Protect your team. Meet the law."
      subtitle="Workers' Compensation is required by law in nearly every U.S. state. It covers medical expenses and lost wages for employees injured on the job — protecting your workforce and shielding your business from workplace liability."
      primaryCta="Get a workers' comp quote"
      secondaryCta="Check state requirements"
    />
    <section className="bi-industry-section bi-industry-section--light">
      <div className="bi-industry-inner">
        <h3>What workers' comp covers</h3>
        <div className="bi-coverage-grid">
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Medical</div>
            <h4>Medical Expenses</h4>
            <p>If an employee is injured or gets sick on the job, a workers' compensation policy covers employee medical expenses and lost wages — from emergency care through ongoing treatment and rehabilitation.</p>
          </div>
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Legal</div>
            <h4>Employer Liability</h4>
            <p>If an employee files a lawsuit due to an injury or illness sustained at the workplace, workers' compensation covers legal defense expenses associated with the claim.</p>
          </div>
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Benefits</div>
            <h4>Survivor Benefits</h4>
            <p>In the tragic event of an employee fatality, the beneficiaries entitled to cash benefits are covered under a workers' compensation policy.</p>
          </div>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--muted">
      <div className="bi-industry-inner">
        <h3>Who needs workers' compensation insurance?</h3>
        <ul className="bi-risk-list">
          <li>
            <div className="bi-risk-item-title">Any business with employees</div>
            <div className="bi-risk-item-body">In nearly all U.S. states, any business that employs individuals — even part-time — is legally required to carry workers' compensation insurance.</div>
          </li>
          <li>
            <div className="bi-risk-item-title">Self-employed professionals and freelancers</div>
            <div className="bi-risk-item-body">Even without traditional employees, self-employed professionals may be required to carry workers' comp to fulfill certain state requirements or contractual obligations with clients.</div>
          </li>
          <li>
            <div className="bi-risk-item-title">Startups and growing teams</div>
            <div className="bi-risk-item-body">As soon as you hire your first W-2 employee, workers' comp requirements kick in. Getting covered early avoids compliance gaps and protects your team from day one.</div>
          </li>
          <li>
            <div className="bi-risk-item-title">Remote and hybrid teams</div>
            <div className="bi-risk-item-body">Workers' comp covers employees regardless of where they work — including home offices. Remote work doesn't eliminate the requirement or the exposure.</div>
          </li>
        </ul>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--light">
      <div className="bi-industry-inner">
        <h3>Why it matters beyond compliance</h3>
        <div className="bi-industry-two-col">
          <div>
            <h4 style={{ fontWeight: 700, color: '#0f172a', marginBottom: '10px' }}>Protect your employees</h4>
            <p className="bi-industry-body">Workers' comp ensures your team gets the medical care they need after a workplace injury without facing financial hardship. It demonstrates that you take your duty of care seriously — which matters for morale, retention, and recruitment.</p>
          </div>
          <div>
            <h4 style={{ fontWeight: 700, color: '#0f172a', marginBottom: '10px' }}>Protect your business</h4>
            <p className="bi-industry-body">Without workers' comp, a single workplace injury could expose your business to uncapped medical bills and legal liability. The policy provides a defined, predictable response to what could otherwise be a financially catastrophic event.</p>
          </div>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--muted">
      <div className="bi-industry-inner">
        <h3>Frequently asked questions</h3>
        <div className="bi-faq">
          <div className="bi-faq-item">
            <h4>Is workers' comp required by law?</h4>
            <p>Workers' compensation is regulated at the state level, and nearly every state requires employers to carry it. Requirements vary by state, industry, and number of employees — BizServe can help you understand your specific obligations.</p>
          </div>
          <div className="bi-faq-item">
            <h4>How do I show proof of a workers' comp policy?</h4>
            <p>A Certificate of Insurance (COI) can be sent instantly to any party — client, state agency, or general contractor — that requires proof of coverage.</p>
          </div>
          <div className="bi-faq-item">
            <h4>Do I need workers' comp if I am self-employed?</h4>
            <p>You may not be legally required as a sole proprietor, but you may need it for certain business contracts or client requirements. It also provides important protection for your own lost wages and medical expenses.</p>
          </div>
          <div className="bi-faq-item">
            <h4>Are company owners covered under a workers' comp policy?</h4>
            <p>Inclusion of coverage for company owners and officers varies by policy and state. Some owners elect to exclude themselves to reduce premium costs. BizServe can walk you through the options for your state.</p>
          </div>
        </div>
      </div>
    </section>

    <CTA />
  </>
);

const CyberPage = ({ onBack }: { onBack?: () => void }) => (
  <>
    <Hero
      showBack
      onBack={onBack}
      title="Your digital operations covered, end to end."
      subtitle="Cyber insurance covers the financial and operational fallout of cyberattacks, data breaches, and tech-driven disruptions — both first-party costs to your own business and third-party liability to others."
      primaryCta="Get a cyber quote"
      secondaryCta="Review my risk exposure"
    />
    <section className="bi-industry-section bi-industry-section--light">
      <div className="bi-industry-inner">
        <h3>What cyber insurance covers</h3>
        <div className="bi-coverage-grid">
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">First-Party</div>
            <h4>Breach Response &amp; Notification</h4>
            <p>Covers the cost of notifying affected individuals, providing credit monitoring, and managing public communications after a data breach.</p>
          </div>
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">First-Party</div>
            <h4>Forensics &amp; Data Recovery</h4>
            <p>Pays for IT forensics to identify how a breach occurred, restore corrupted or deleted data, and remediate compromised systems.</p>
          </div>
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">First-Party</div>
            <h4>Cyber Extortion &amp; Ransomware</h4>
            <p>If malicious malware is installed on your systems and a ransom is demanded, cyber insurance covers the associated financial losses and ransom payments where permissible.</p>
          </div>
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">First-Party</div>
            <h4>Business Interruption</h4>
            <p>Covers lost revenue and ongoing fixed expenses when a cyberattack forces a temporary shutdown or significant disruption of your operations.</p>
          </div>
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Third-Party</div>
            <h4>Privacy Liability &amp; Regulatory Penalties</h4>
            <p>Covers legal defense and regulatory fines when a data breach triggers investigations under GDPR, HIPAA, CCPA, or other privacy regulations.</p>
          </div>
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Third-Party</div>
            <h4>Legal Defense &amp; Liability</h4>
            <p>If a customer or partner sues your business following a breach or cyber incident, the policy covers legal defense costs and settlements.</p>
          </div>
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Third-Party</div>
            <h4>Fraud &amp; Social Engineering</h4>
            <p>Covers financial losses from funds transfer fraud, phishing attacks, and social engineering schemes that trick employees into wiring money or disclosing credentials.</p>
          </div>
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Content</div>
            <h4>Media &amp; Content Liability</h4>
            <p>Covers claims arising from content published on your website or digital platforms — including defamation, copyright infringement, and privacy violations.</p>
          </div>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--muted">
      <div className="bi-industry-inner">
        <h3>What cyber insurance does NOT cover</h3>
        <div className="bi-industry-two-col">
          <ul className="bi-risk-list">
            <li>
              <div className="bi-risk-item-title">Intentional or criminal acts by the insured</div>
              <div className="bi-risk-item-body">Deliberate damage or fraud perpetrated by the policyholder or its principals is excluded.</div>
            </li>
            <li>
              <div className="bi-risk-item-title">Nation-state attacks and acts of war</div>
              <div className="bi-risk-item-body">Cyberattacks attributed to nation-state actors or classified as acts of warfare are typically excluded.</div>
            </li>
          </ul>
          <ul className="bi-risk-list">
            <li>
              <div className="bi-risk-item-title">Prior known incidents</div>
              <div className="bi-risk-item-body">Incidents that were known or reported before the policy's inception date are not covered.</div>
            </li>
            <li>
              <div className="bi-risk-item-title">Hardware replacement</div>
              <div className="bi-risk-item-body">Physical replacement of servers, computers, or network hardware damaged or destroyed is generally excluded — that falls under Property coverage.</div>
            </li>
          </ul>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--light">
      <div className="bi-industry-inner">
        <h3>Who needs cyber insurance?</h3>
        <ul className="bi-risk-list">
          <li>
            <div className="bi-risk-item-title">Any business that stores customer or employee data</div>
            <div className="bi-risk-item-body">If you hold personally identifiable information — names, emails, payment cards, health records — you have breach notification obligations and liability exposure.</div>
          </li>
          <li>
            <div className="bi-risk-item-title">SaaS, tech, and digital-first companies</div>
            <div className="bi-risk-item-body">Your product IS the digital system. A breach, outage, or ransomware event can simultaneously trigger customer liability and business interruption losses.</div>
          </li>
          <li>
            <div className="bi-risk-item-title">E-commerce merchants and payment processors</div>
            <div className="bi-risk-item-body">PCI-DSS obligations, payment card data, and customer transaction records create significant cyber exposure for online sellers.</div>
          </li>
          <li>
            <div className="bi-risk-item-title">Professional services firms with client data access</div>
            <div className="bi-risk-item-body">Accountants, lawyers, consultants, and HR firms that hold sensitive client data face both legal liability and regulatory risk after a breach.</div>
          </li>
        </ul>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--muted">
      <div className="bi-industry-inner">
        <h3>Real-world scenarios</h3>
        <div className="bi-scenarios">
          <div className="bi-scenario">
            <div className="bi-scenario-label">Scenario</div>
            <h4>Data Breach — Customer Records Exposed</h4>
            <p><strong>What happened:</strong> An attacker gains unauthorized access to your database, exposing tens of thousands of customer records including names, emails, and payment information.</p>
            <p><strong>How coverage responds:</strong> Cyber policy covers forensics to identify the intrusion, legal notification to affected customers, credit monitoring services, and any regulatory fines.</p>
          </div>
          <div className="bi-scenario">
            <div className="bi-scenario-label">Scenario</div>
            <h4>System Hack — Infrastructure Compromised</h4>
            <p><strong>What happened:</strong> A threat actor compromises your cloud infrastructure, causing service outages and leaking proprietary data belonging to enterprise clients.</p>
            <p><strong>How coverage responds:</strong> Cyber policy covers business interruption losses, legal defense against client claims, and forensics costs to restore your environment.</p>
          </div>
          <div className="bi-scenario">
            <div className="bi-scenario-label">Scenario</div>
            <h4>Ransomware — Operations Held Hostage</h4>
            <p><strong>What happened:</strong> Malware encrypts your critical files and systems. Attackers demand a six-figure ransom to restore access.</p>
            <p><strong>How coverage responds:</strong> Cyber policy covers the ransom payment (where permissible), data restoration, and the business interruption losses during downtime.</p>
          </div>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--light">
      <div className="bi-industry-inner">
        <h3>Frequently asked questions</h3>
        <div className="bi-faq">
          <div className="bi-faq-item">
            <h4>Are there any requirements to get cyber coverage?</h4>
            <p>Requirements vary by industry and carrier. Insurers typically review your security controls, past incidents, and data sensitivity. Having MFA, endpoint protection, and data backups in place generally improves your terms and pricing.</p>
          </div>
          <div className="bi-faq-item">
            <h4>Does cyber cover ransom payments?</h4>
            <p>In most cases, the carrier will cover the cost of a ransom payment where legally permissible, rather than dispute the attack. Coverage terms vary by carrier and policy form.</p>
          </div>
          <div className="bi-faq-item">
            <h4>Does my General Liability policy cover cyber incidents?</h4>
            <p>No. GL does not respond to data breaches, ransomware, or digital business interruption. Cyber insurance fills the gap that GL and Property policies leave in your digital risk exposure.</p>
          </div>
          <div className="bi-faq-item">
            <h4>How quickly does cyber incident response begin?</h4>
            <p>BizServe partners with specialized cyber breach coaches who respond within 4 hours, 24/7/365 — handling forensics, public relations, notification, and remediation so you can focus on recovery.</p>
          </div>
        </div>
      </div>
    </section>

    <CTA />
  </>
);

const EoPage = ({ onBack }: { onBack?: () => void }) => (
  <>
    <Hero
      showBack
      onBack={onBack}
      title="When clients claim your work caused their loss."
      subtitle="Errors &amp; Omissions (E&amp;O) — also called Professional Liability — covers your business against client disputes arising from professional negligence, mistakes, or missed deadlines. Defense costs are covered regardless of whether the claim has merit."
      primaryCta="Get an E&O quote"
      secondaryCta="Review my contracts"
    />
    <section className="bi-industry-section bi-industry-section--light">
      <div className="bi-industry-inner">
        <h3>What E&amp;O covers</h3>
        <div className="bi-coverage-grid">
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Negligence</div>
            <h4>Professional Negligence</h4>
            <p>A client may claim that your business was negligent in handling a project or assignment. E&amp;O covers legal defense costs and damages — whether or not the allegation is valid.</p>
          </div>
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Delivery</div>
            <h4>Unfulfilled Task</h4>
            <p>If a client accuses your business of not fulfilling a contracted task or deliverable, E&amp;O covers consequential damages and the costs of defending the claim.</p>
          </div>
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Errors</div>
            <h4>Professional Mistakes</h4>
            <p>A client may claim that a professional error in your work caused them financial loss. E&amp;O responds to those claims, covering legal counsel and reimbursement of damages where covered.</p>
          </div>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--muted">
      <div className="bi-industry-inner">
        <h3>E&amp;O vs. General Liability — what's the difference?</h3>
        <div className="bi-industry-two-col">
          <div>
            <h4 style={{ fontWeight: 700, color: '#0f172a', marginBottom: '10px' }}>Errors &amp; Omissions (E&amp;O)</h4>
            <p className="bi-industry-body">E&amp;O covers financial losses that arise from your professional services, advice, or work product. If a client claims your recommendations, software, or analysis caused them to lose money, E&amp;O is what responds. It's about the quality of your professional output.</p>
          </div>
          <div>
            <h4 style={{ fontWeight: 700, color: '#0f172a', marginBottom: '10px' }}>General Liability (GL)</h4>
            <p className="bi-industry-body">GL covers physical harm to people and property — slip-and-falls, property damage, advertising injury. GL does not respond to client claims about the quality of your work or the financial impact of your advice. Both policies are typically needed.</p>
          </div>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--light">
      <div className="bi-industry-inner">
        <h3>Who needs professional liability insurance?</h3>
        <ul className="bi-risk-list">
          <li>
            <div className="bi-risk-item-title">Technology companies and software developers</div>
            <div className="bi-risk-item-body">When your platform fails, underperforms, or misses SLAs, clients may allege financial harm. Tech E&amp;O is the standard coverage for SaaS companies, dev shops, and digital product teams.</div>
          </li>
          <li>
            <div className="bi-risk-item-title">Consultants and management advisors</div>
            <div className="bi-risk-item-body">Strategy, operations, and management consultants are routinely required by enterprise clients to carry E&amp;O before signing an SOW or MSA.</div>
          </li>
          <li>
            <div className="bi-risk-item-title">Creative and marketing agencies</div>
            <div className="bi-risk-item-body">Agencies can face claims for missed campaign targets, delayed deliverables, or copyright issues in work product. E&amp;O covers these professional exposures.</div>
          </li>
          <li>
            <div className="bi-risk-item-title">Accountants, bookkeepers, and financial advisors</div>
            <div className="bi-risk-item-body">Financial professionals carry significant liability for the advice they give. E&amp;O (often called Professional Liability in this context) is essential for anyone handling client funds or financial records.</div>
          </li>
          <li>
            <div className="bi-risk-item-title">IT and managed service providers</div>
            <div className="bi-risk-item-body">MSPs, IT consultants, and cloud service providers are exposed to claims when systems they manage are compromised or when upgrades cause client disruption.</div>
          </li>
        </ul>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--muted">
      <div className="bi-industry-inner">
        <h3>Frequently asked questions</h3>
        <div className="bi-faq">
          <div className="bi-faq-item">
            <h4>Is professional liability required by law?</h4>
            <p>Requirements vary by state and profession. However, many enterprise clients and government contracts require proof of E&amp;O as a condition of doing business. It's also critical protection regardless of legal mandate.</p>
          </div>
          <div className="bi-faq-item">
            <h4>How do I show proof of a professional liability policy?</h4>
            <p>A Certificate of Insurance (COI) is your proof of coverage and can be sent instantly to any client or contract counterparty that requires it. BizServe generates COIs in seconds.</p>
          </div>
          <div className="bi-faq-item">
            <h4>Can I add additional insureds to my E&amp;O policy?</h4>
            <p>Yes — an additional insured is any third party listed as a covered party under your policy. Some client contracts require this. BizServe can help you structure additional insured endorsements quickly.</p>
          </div>
          <div className="bi-faq-item">
            <h4>Does E&amp;O cover my employees?</h4>
            <p>Yes. Employees directly related to your business and acting within the scope of their role are typically covered under your professional liability policy.</p>
          </div>
          <div className="bi-faq-item">
            <h4>Is professional liability the same as E&amp;O insurance?</h4>
            <p>Yes — "Professional Liability" and "Errors &amp; Omissions (E&amp;O)" are interchangeable terms describing the same coverage. In tech, it's often called "Tech E&amp;O." The underlying protection is the same.</p>
          </div>
        </div>
      </div>
    </section>

    <CTA />
  </>
);

/* ─────────────────────────────────────────────────────────────
   COVERAGE PAGES 6–9
───────────────────────────────────────────────────────────── */

const DAndOPage = ({ onBack }: { onBack?: () => void }) => (
  <>
    <Hero
      showBack
      onBack={onBack}
      title="Personal protection for the people building the company."
      subtitle="Directors &amp; Officers (D&amp;O) insurance protects founders, executives, and board members when they are sued over management decisions — covering legal defense, settlements, and personal financial exposure. In 2024, there were 225 securities-related lawsuits filed against U.S. companies."
      primaryCta="Get a D&O quote"
      secondaryCta="Talk to an advisor"
    />
    <section className="bi-industry-section bi-industry-section--light">
      <div className="bi-industry-inner">
        <h3>The three components of D&amp;O</h3>
        <div className="bi-coverage-grid">
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Side A</div>
            <h4>Individual Protection</h4>
            <p>Side A covers individual directors and officers directly when the company is unable to indemnify them — for example, in insolvency. Protects personal assets when the company cannot step in.</p>
          </div>
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Side B</div>
            <h4>Company Reimbursement</h4>
            <p>Side B reimburses the company when it indemnifies its directors and officers after a covered claim. This is the most commonly triggered component of a D&amp;O policy.</p>
          </div>
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Side C</div>
            <h4>Entity Coverage</h4>
            <p>Side C covers the company itself when it is named alongside its directors and officers in certain securities claims. Particularly important for public companies and those pursuing IPO.</p>
          </div>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--muted">
      <div className="bi-industry-inner">
        <h3>What D&amp;O covers</h3>
        <ul className="bi-risk-list">
          <li>
            <div className="bi-risk-item-title">Legal defense costs</div>
            <div className="bi-risk-item-body">Attorney fees, court costs, and other litigation expenses when directors or officers are sued — regardless of outcome.</div>
          </li>
          <li>
            <div className="bi-risk-item-title">Settlements and judgments</div>
            <div className="bi-risk-item-body">Financial settlements or court-ordered judgments arising from covered claims, up to policy limits.</div>
          </li>
          <li>
            <div className="bi-risk-item-title">Investor and shareholder suits</div>
            <div className="bi-risk-item-body">Claims by investors or shareholders alleging mismanagement, misrepresentation, or breach of fiduciary duty in connection with financing rounds or governance decisions.</div>
          </li>
          <li>
            <div className="bi-risk-item-title">Regulatory investigations</div>
            <div className="bi-risk-item-body">Government and regulatory investigations involving the conduct of directors and officers — including SEC inquiries, FTC investigations, and state-level proceedings.</div>
          </li>
          <li>
            <div className="bi-risk-item-title">Misstatements and reporting errors</div>
            <div className="bi-risk-item-body">Claims arising from inaccurate statements in financial reports, pitch decks, or regulatory filings that allegedly misled investors or regulators.</div>
          </li>
        </ul>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--light">
      <div className="bi-industry-inner">
        <h3>What D&amp;O does NOT cover</h3>
        <ul className="bi-risk-list">
          <li>
            <div className="bi-risk-item-title">Employment claims (covered by EPLI)</div>
            <div className="bi-risk-item-body">Wrongful termination, discrimination, and harassment claims are covered by Employment Practices Liability — not D&amp;O.</div>
          </li>
          <li>
            <div className="bi-risk-item-title">Professional service errors (covered by E&amp;O)</div>
            <div className="bi-risk-item-body">Claims about the quality of your services or products are covered by E&amp;O/Tech E&amp;O, not D&amp;O.</div>
          </li>
          <li>
            <div className="bi-risk-item-title">Intentional fraud (after adjudication)</div>
            <div className="bi-risk-item-body">Once a court determines that fraud or intentional wrongdoing occurred, coverage for that specific conduct is excluded.</div>
          </li>
          <li>
            <div className="bi-risk-item-title">Bodily injury and property damage (covered by GL)</div>
            <div className="bi-risk-item-body">Physical harm to people and property is covered by General Liability — D&amp;O addresses management decisions, not operational incidents.</div>
          </li>
        </ul>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--muted">
      <div className="bi-industry-inner">
        <h3>D&amp;O vs. other policies</h3>
        <div className="bi-industry-two-col">
          <ul className="bi-risk-list">
            <li>
              <div className="bi-risk-item-title">D&amp;O vs. E&amp;O</div>
              <div className="bi-risk-item-body">D&amp;O covers management decisions and governance; E&amp;O covers errors in professional services and products. Different triggers, different claims — most funded companies need both.</div>
            </li>
            <li>
              <div className="bi-risk-item-title">D&amp;O vs. EPLI</div>
              <div className="bi-risk-item-body">D&amp;O covers decisions made by leadership in their executive role; EPLI covers allegations of wrongdoing in the employment relationship (termination, discrimination, harassment).</div>
            </li>
          </ul>
          <ul className="bi-risk-list">
            <li>
              <div className="bi-risk-item-title">D&amp;O vs. Cyber</div>
              <div className="bi-risk-item-body">D&amp;O covers management liability; Cyber covers data breaches and digital incidents. A security breach could trigger both if investors sue management over inadequate cybersecurity governance.</div>
            </li>
            <li>
              <div className="bi-risk-item-title">D&amp;O vs. GL</div>
              <div className="bi-risk-item-body">GL covers physical harm to third parties from your operations; D&amp;O covers personal liability of your leaders for how they run the company. Entirely different coverage triggers.</div>
            </li>
          </ul>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--light">
      <div className="bi-industry-inner">
        <h3>Frequently asked questions</h3>
        <div className="bi-faq">
          <div className="bi-faq-item">
            <h4>When do startups need D&amp;O insurance?</h4>
            <p>Most institutional investors require D&amp;O before closing a seed or Series A round. Even before that, once you have a board of directors or advisory board, D&amp;O provides important personal protection for those individuals.</p>
          </div>
          <div className="bi-faq-item">
            <h4>Do investors require D&amp;O?</h4>
            <p>Yes — venture capital and other institutional investors frequently require D&amp;O coverage as a condition of finalizing an investment. It protects the individuals they are backing and demonstrates sound governance.</p>
          </div>
          <div className="bi-faq-item">
            <h4>What happens without D&amp;O insurance?</h4>
            <p>Without D&amp;O, founders and board members may be personally liable for damages from investor lawsuits or governance disputes. This can result in significant personal financial losses and reputational damage — and can make it harder to attract future investors or board members.</p>
          </div>
          <div className="bi-faq-item">
            <h4>Does D&amp;O cover cap table disputes?</h4>
            <p>D&amp;O can respond to shareholder lawsuits related to ownership disputes, equity distributions, M&amp;A, or changes in corporate governance — these are among the most common startup-related D&amp;O claims.</p>
          </div>
        </div>
      </div>
    </section>

    <CTA />
  </>
);

const ManagementLiabilityPage = ({ onBack }: { onBack?: () => void }) => (
  <>
    <Hero
      showBack
      onBack={onBack}
      title="Protect your founders, your board, and your future."
      subtitle="Management Liability insurance bundles Directors &amp; Officers, Employment Practices Liability, and Fiduciary &amp; Crime coverages into one comprehensive policy — protecting your leadership team, cap table, and the long-term future of your business."
      primaryCta="Get a management liability quote"
      secondaryCta="Talk to an advisor"
    />
    <section className="bi-industry-section bi-industry-section--light">
      <div className="bi-industry-inner">
        <h3>What management liability bundles</h3>
        <div className="bi-coverage-grid">
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">D&amp;O</div>
            <h4>Directors &amp; Officers</h4>
            <p>Financial protection for a company's management team against claims related to management decisions. Covers legal defense costs, settlements, and judgments — protecting personal assets against lawsuits alleging mismanagement or breach of fiduciary duty.</p>
          </div>
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">EPL</div>
            <h4>Employment Practices Liability</h4>
            <p>Protection against claims related to employment practices, such as discrimination or wrongful termination. Covers legal defense costs, settlements, and judgments — and can include coverage for wage disputes and retaliation claims.</p>
          </div>
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Crime</div>
            <h4>Fiduciary &amp; Crime</h4>
            <p>Financial protection against claims related to employee theft, fraud, or embezzlement. Also protects against claims alleging breach of fiduciary duty or errors in employee benefits plans.</p>
          </div>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--muted">
      <div className="bi-industry-inner">
        <h3>Why startups and VC-backed companies need it</h3>
        <div className="bi-industry-two-col">
          <div>
            <h4 style={{ fontWeight: 700, color: '#0f172a', marginBottom: '10px' }}>Investor requirements</h4>
            <p className="bi-industry-body">Venture capital and other institutional investors often require proof of management liability insurance to finalize due diligence and close an investment round. It shows proper risk management and protects the executives and board members they are backing.</p>
          </div>
          <div>
            <h4 style={{ fontWeight: 700, color: '#0f172a', marginBottom: '10px' }}>Personal financial protection</h4>
            <p className="bi-industry-body">Without management liability coverage, executives, founders, and board members may be personally liable for damages from lawsuits. This can result in significant personal financial losses — and it may make it harder to attract seasoned board members or advisors willing to put their names on your governance documents.</p>
          </div>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--light">
      <div className="bi-industry-inner">
        <h3>Real scenarios</h3>
        <div className="bi-scenarios">
          <div className="bi-scenario">
            <div className="bi-scenario-label">Scenario</div>
            <h4>Cap Table Dispute</h4>
            <p><strong>What happened:</strong> An early investor files suit claiming misrepresentation during a fundraising round, alleging equity was improperly diluted during a recapitalization.</p>
            <p><strong>How coverage responds:</strong> D&amp;O responds to cover legal defense costs and, where covered, settlements tied to alleged mismanagement or breach of fiduciary duty.</p>
          </div>
          <div className="bi-scenario">
            <div className="bi-scenario-label">Scenario</div>
            <h4>Wrongful Termination Claim</h4>
            <p><strong>What happened:</strong> A former employee alleges they were wrongfully terminated after raising an internal compliance concern, filing an EEOC complaint against the company.</p>
            <p><strong>How coverage responds:</strong> EPL covers legal defense costs, EEOC proceedings, and settlements related to the employment dispute.</p>
          </div>
          <div className="bi-scenario">
            <div className="bi-scenario-label">Scenario</div>
            <h4>Employee Theft</h4>
            <p><strong>What happened:</strong> A trusted finance employee embezzles company funds over several months through falsified expense reports.</p>
            <p><strong>How coverage responds:</strong> The Crime component of the management liability bundle covers direct financial losses from employee theft and fraud.</p>
          </div>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--muted">
      <div className="bi-industry-inner">
        <h3>Frequently asked questions</h3>
        <div className="bi-faq">
          <div className="bi-faq-item">
            <h4>What happens if I don't have management liability insurance?</h4>
            <p>Executives, founders, and board members may be personally liable for damages. This can result in significant financial losses and reputational damage — and may make it more difficult to attract investors, board members, and partners.</p>
          </div>
          <div className="bi-faq-item">
            <h4>Why do startups need management liability insurance?</h4>
            <p>To protect executives and board members from personal financial loss from lawsuits and legal claims. It also helps protect the company's assets and reputation and promotes sound corporate governance — particularly important as you scale and add institutional investors.</p>
          </div>
          <div className="bi-faq-item">
            <h4>Are startups required to have management liability coverage?</h4>
            <p>Venture capital investors often require it prior to finalizing due diligence and closing an investment. It shows proper risk management and protects the executives and board members they are investing alongside.</p>
          </div>
          <div className="bi-faq-item">
            <h4>How do I show proof of a management liability policy?</h4>
            <p>A Certificate of Insurance (COI) can be presented to any investor or counterparty that requires proof. BizServe also provides your full management liability policy documentation for review during due diligence.</p>
          </div>
        </div>
      </div>
    </section>

    <CTA />
  </>
);

const EmploymentPracticesPage = ({ onBack }: { onBack?: () => void }) => (
  <>
    <Hero
      showBack
      onBack={onBack}
      title="When employment disputes become legal claims."
      subtitle="Employment Practices Liability (EPL) covers your business against claims from employees or contractors alleging discrimination, wrongful termination, harassment, or wage disputes — and includes access to a legal hotline to resolve issues before they escalate."
      primaryCta="Get an EPL quote"
      secondaryCta="Talk to an advisor"
    />
    <section className="bi-industry-section bi-industry-section--light">
      <div className="bi-industry-inner">
        <h3>What EPL covers</h3>
        <div className="bi-coverage-grid">
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Termination</div>
            <h4>Wrongful Termination</h4>
            <p>Claims by current or former employees alleging they were dismissed without cause, in violation of employment law, or as retaliation for protected activity.</p>
          </div>
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Discrimination</div>
            <h4>Discrimination Claims</h4>
            <p>Allegations of unlawful discrimination based on age, race, gender, religion, disability, or other protected characteristics — in hiring, promotion, compensation, or termination.</p>
          </div>
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Harassment</div>
            <h4>Workplace Harassment</h4>
            <p>Claims of sexual harassment, hostile work environment, or other forms of workplace harassment brought by employees, contractors, or third parties.</p>
          </div>
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Wage &amp; Hour</div>
            <h4>Wage Disputes &amp; Retaliation</h4>
            <p>Claims related to unpaid wages, overtime violations, and retaliation against employees who raise workplace concerns or file complaints with regulatory agencies.</p>
          </div>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--muted">
      <div className="bi-industry-inner">
        <h3>The EPL Legal Hotline — early intervention saves money</h3>
        <div className="bi-industry-two-col">
          <div>
            <h4 style={{ fontWeight: 700, color: '#0f172a', marginBottom: '10px' }}>Three free hours of employment law advice</h4>
            <p className="bi-industry-body">BizServe partners with top-tier employment law firms so that when an employee issue arises — a contentious departure, a harassment allegation, an EEOC complaint — you get immediate access to qualified legal counsel. Policyholders receive three free hours of employment law advice through the hotline.</p>
          </div>
          <div>
            <h4 style={{ fontWeight: 700, color: '#0f172a', marginBottom: '10px' }}>25% reduction in out-of-pocket claims costs</h4>
            <p className="bi-industry-body">Using the legal hotline early — before a dispute becomes a formal claim — can reduce your out-of-pocket costs by at least 25% (where a retention reduction endorsement is active). Early legal advice is the most cost-effective tool for preventing employment disputes from escalating.</p>
          </div>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--light">
      <div className="bi-industry-inner">
        <h3>Who needs EPL insurance?</h3>
        <ul className="bi-risk-list">
          <li>
            <div className="bi-risk-item-title">Any business with employees</div>
            <div className="bi-risk-item-body">From your first hire to your hundredth, employment claims can arise at any stage. The EEOC receives over 70,000 workplace discrimination charges annually.</div>
          </li>
          <li>
            <div className="bi-risk-item-title">Companies with remote or hybrid teams</div>
            <div className="bi-risk-item-body">Multi-state employment relationships introduce complex wage-and-hour and discrimination law exposure. EPL provides protection across all jurisdictions where your employees work.</div>
          </li>
          <li>
            <div className="bi-risk-item-title">Startups going through rapid hiring or layoffs</div>
            <div className="bi-risk-item-body">High-growth hiring and reduction-in-force events are among the highest-risk periods for employment claims. EPL is critical coverage during these transitions.</div>
          </li>
          <li>
            <div className="bi-risk-item-title">Businesses that engage independent contractors</div>
            <div className="bi-risk-item-body">Contractor misclassification and disputes over the nature of the working relationship can trigger employment-style claims even without traditional W-2 employees.</div>
          </li>
        </ul>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--muted">
      <div className="bi-industry-inner">
        <h3>Frequently asked questions</h3>
        <div className="bi-faq">
          <div className="bi-faq-item">
            <h4>Does EPL cover contractors as well as employees?</h4>
            <p>Yes — EPL policies typically cover claims from current and former employees, job applicants, and in many cases, independent contractors who allege employment-related wrongdoing.</p>
          </div>
          <div className="bi-faq-item">
            <h4>What is the EPL Legal Hotline?</h4>
            <p>The hotline gives you direct access to experienced employment counsel when a workplace incident occurs. Three free hours of advice are included with your policy, helping you respond to issues early — before they become formal claims.</p>
          </div>
          <div className="bi-faq-item">
            <h4>Does EPL cover wage and hour claims?</h4>
            <p>Many EPL policies include coverage for wage and hour disputes and retaliation claims related to employees raising wage concerns. Confirm the specific terms with your BizServe advisor.</p>
          </div>
          <div className="bi-faq-item">
            <h4>Is EPL part of management liability?</h4>
            <p>Yes — EPL is one of the core components of a Management Liability bundle, alongside Directors &amp; Officers and Fiduciary &amp; Crime coverages. You can purchase EPL as a standalone policy or as part of the bundle.</p>
          </div>
        </div>
      </div>
    </section>

    <CTA />
  </>
);

const AiInsurancePage = ({ onBack }: { onBack?: () => void }) => (
  <>
    <Hero
      showBack
      onBack={onBack}
      title="The first coverage built for what AI actually does."
      subtitle="AI Insurance is purpose-built protection for damages caused by AI products and services — including model hallucinations, bias claims, and downstream decision errors that fall outside traditional Tech E&amp;O scope."
      primaryCta="Get an AI insurance quote"
      secondaryCta="Talk to an advisor"
    />
    <section className="bi-industry-section bi-industry-section--light">
      <div className="bi-industry-inner">
        <h3>What AI insurance covers</h3>
        <div className="bi-coverage-grid">
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Hallucinations</div>
            <h4>Model Hallucination Liability</h4>
            <p>When an AI model generates false, misleading, or fabricated outputs that cause a user or third party to suffer financial or reputational harm, AI Insurance covers the resulting liability.</p>
          </div>
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Bias</div>
            <h4>Bias &amp; Discrimination Claims</h4>
            <p>When an AI system produces discriminatory outcomes — in hiring, lending, diagnostics, or recommendations — AI Insurance covers the legal defense and regulatory exposure these claims create.</p>
          </div>
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Decisions</div>
            <h4>Downstream Decision Errors</h4>
            <p>AI-powered tools that automate consequential decisions — medical triage, credit scoring, legal analysis — can cause harm when the underlying model makes an error. AI Insurance responds to those downstream liability claims.</p>
          </div>
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Products</div>
            <h4>AI Product Liability</h4>
            <p>For companies that ship AI-powered products or APIs, AI Insurance covers product liability claims arising from failures in the AI component — distinct from traditional product defect claims covered by GL.</p>
          </div>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--muted">
      <div className="bi-industry-inner">
        <h3>When standard Tech E&amp;O isn't enough</h3>
        <div className="bi-industry-two-col">
          <div>
            <h4 style={{ fontWeight: 700, color: '#0f172a', marginBottom: '10px' }}>The gap in traditional E&amp;O</h4>
            <p className="bi-industry-body">Traditional Tech E&amp;O was designed for software that behaves deterministically — where a bug produces a traceable error. AI systems are probabilistic by nature: the same input can produce different outputs, and the "error" may be a statistically plausible but factually wrong result. Standard E&amp;O policies often don't contemplate these failure modes.</p>
          </div>
          <div>
            <h4 style={{ fontWeight: 700, color: '#0f172a', marginBottom: '10px' }}>What AI Insurance adds</h4>
            <p className="bi-industry-body">AI Insurance explicitly covers the unique risk profile of AI-driven products: hallucinated outputs, model drift, training data bias, and automated decisions with real-world consequences. It fills the gap between what traditional E&amp;O covers and the actual liability profile of modern AI products.</p>
          </div>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--light">
      <div className="bi-industry-inner">
        <h3>Who needs AI insurance?</h3>
        <ul className="bi-risk-list">
          <li>
            <div className="bi-risk-item-title">Companies with AI assistants and chatbots</div>
            <div className="bi-risk-item-body">Customer-facing AI that answers questions, recommends products, or provides guidance can generate harmful hallucinations — creating liability for the company that deployed the model.</div>
          </li>
          <li>
            <div className="bi-risk-item-title">Recommendation engines and personalization platforms</div>
            <div className="bi-risk-item-body">AI systems that influence purchasing decisions, content consumption, or financial behavior carry bias and discrimination exposure — particularly in regulated industries.</div>
          </li>
          <li>
            <div className="bi-risk-item-title">Automated decision-making tools</div>
            <div className="bi-risk-item-body">AI used in hiring, lending, insurance underwriting, or healthcare triage faces significant regulatory scrutiny and legal exposure when decisions are challenged as discriminatory or erroneous.</div>
          </li>
          <li>
            <div className="bi-risk-item-title">LLM product companies and AI API providers</div>
            <div className="bi-risk-item-body">Companies that expose large language models through APIs or embed them in third-party workflows face compound liability — both from direct users and from downstream applications built on top of their models.</div>
          </li>
        </ul>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--muted">
      <div className="bi-industry-inner">
        <h3>Real scenarios</h3>
        <div className="bi-scenarios">
          <div className="bi-scenario">
            <div className="bi-scenario-label">Scenario</div>
            <h4>Medical Diagnostics Startup — E&amp;O Claim</h4>
            <p><strong>What happened:</strong> An AI-powered diagnostic tool provides an erroneous recommendation that leads to a delayed diagnosis and patient harm. The healthcare provider sues the AI company for product liability and E&amp;O.</p>
            <p><strong>How coverage responds:</strong> AI Insurance covers legal defense and, where applicable, settlements arising from the AI product's role in the adverse outcome.</p>
          </div>
          <div className="bi-scenario">
            <div className="bi-scenario-label">Scenario</div>
            <h4>Healthcare IT Startup — E&amp;O + Cyber Claim</h4>
            <p><strong>What happened:</strong> An AI-assisted clinical workflow tool misclassifies patient records and exposes PHI in the process — triggering both an E&amp;O claim for the workflow error and a cyber claim for the data exposure.</p>
            <p><strong>How coverage responds:</strong> AI Insurance responds to the E&amp;O component while Cyber coverage handles the breach response, notification, and regulatory penalties.</p>
          </div>
          <div className="bi-scenario">
            <div className="bi-scenario-label">Scenario</div>
            <h4>AI Hiring Tool — Bias Claim</h4>
            <p><strong>What happened:</strong> An AI-powered resume screening tool is alleged to have systematically disadvantaged candidates of a protected class, resulting in an EEOC investigation and civil lawsuit against the employer and the AI vendor.</p>
            <p><strong>How coverage responds:</strong> AI Insurance covers legal defense costs and settlements related to the bias claim against the AI product company.</p>
          </div>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--light">
      <div className="bi-industry-inner">
        <h3>Frequently asked questions</h3>
        <div className="bi-faq">
          <div className="bi-faq-item">
            <h4>Is AI Insurance separate from Tech E&amp;O?</h4>
            <p>Yes — AI Insurance is a distinct coverage designed specifically for the failure modes of AI products: hallucinations, model bias, and probabilistic decision errors that are not well-covered by standard Tech E&amp;O policy forms.</p>
          </div>
          <div className="bi-faq-item">
            <h4>What AI use cases are most likely to trigger claims?</h4>
            <p>High-risk use cases include AI in healthcare (diagnostics, triage), financial services (credit scoring, underwriting), HR (hiring tools), and legal (contract analysis). Any AI that makes or influences consequential decisions carries significant liability exposure.</p>
          </div>
          <div className="bi-faq-item">
            <h4>Is AI Insurance required?</h4>
            <p>Not yet universally required by law, but enterprise clients increasingly require it as a contractual condition — particularly in regulated industries like healthcare and financial services. Regulatory frameworks like the EU AI Act are accelerating this trend.</p>
          </div>
          <div className="bi-faq-item">
            <h4>Can I add AI Insurance to an existing Tech E&amp;O policy?</h4>
            <p>In some cases, AI-specific endorsements can be added to an existing E&amp;O policy. In others, a standalone AI Insurance policy provides broader and more explicit coverage. BizServe will help you determine the right structure for your product and risk profile.</p>
          </div>
        </div>
      </div>
    </section>

    <CTA />
  </>
);

/* ─────────────────────────────────────────────────────────────
   RISK SERVICE PAGES (3)
───────────────────────────────────────────────────────────── */

const ContractReviewsPage = ({ onBack }: { onBack?: () => void }) => (
  <>
    <Hero
      showBack
      onBack={onBack}
      title="Get limits that won't limit your deal."
      subtitle="From MSA insurance sections and vendor questionnaires to commercial leases and fundraising requirements — BizServe translates dense contract language into a clear, actionable coverage roadmap so you never stall a deal over an insurance clause."
      primaryCta="Get coverage aligned to my contracts"
      secondaryCta="Talk to an advisor"
    />
    <section className="bi-industry-section bi-industry-section--light">
      <div className="bi-industry-inner">
        <h3>The most common contract insurance requirements</h3>
        <ul className="bi-risk-list">
          <li>
            <div className="bi-risk-item-title">General Liability limits</div>
            <div className="bi-risk-item-body">75% of commercial leases require General Liability. Enterprise MSAs and SaaS vendor agreements commonly specify minimum GL limits — often $1M/$2M or higher for regulated industries.</div>
          </li>
          <li>
            <div className="bi-risk-item-title">Additional insured endorsements</div>
            <div className="bi-risk-item-body">Many contracts require you to add the counterparty as an "additional insured" on your GL policy — meaning your coverage extends to protect them for certain claims arising from your operations.</div>
          </li>
          <li>
            <div className="bi-risk-item-title">Primary and non-contributory language</div>
            <div className="bi-risk-item-body">Enterprise clients often require your policy to be "primary and non-contributory" — meaning your insurance responds first, before any coverage the client has, and without seeking contribution from their policies.</div>
          </li>
          <li>
            <div className="bi-risk-item-title">Errors &amp; Omissions (E&amp;O)</div>
            <div className="bi-risk-item-body">Technology service agreements, consulting engagements, and managed service contracts routinely require E&amp;O (Tech E&amp;O) coverage — often with limits specified in the SOW or MSA.</div>
          </li>
          <li>
            <div className="bi-risk-item-title">Cyber for data access</div>
            <div className="bi-risk-item-body">Any contract that gives you access to client data — even read-only — is increasingly accompanied by a cyber insurance requirement. Requirements have become standard in enterprise vendor onboarding.</div>
          </li>
        </ul>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--muted">
      <div className="bi-industry-inner">
        <h3>How BizServe reviews your contracts</h3>
        <div className="bi-industry-two-col">
          <div>
            <h4 style={{ fontWeight: 700, color: '#0f172a', marginBottom: '10px' }}>What we look for</h4>
            <ul className="bi-risk-list">
              <li><div className="bi-risk-item-title">Required coverages and policy types</div></li>
              <li><div className="bi-risk-item-title">Minimum limit requirements</div></li>
              <li><div className="bi-risk-item-title">Additional insured and waiver of subrogation requests</div></li>
              <li><div className="bi-risk-item-title">Primary/non-contributory language</div></li>
              <li><div className="bi-risk-item-title">Notice of cancellation clauses</div></li>
              <li><div className="bi-risk-item-title">Indemnification and hold harmless provisions</div></li>
            </ul>
          </div>
          <div>
            <h4 style={{ fontWeight: 700, color: '#0f172a', marginBottom: '10px' }}>What we give you</h4>
            <ul className="bi-risk-list">
              <li><div className="bi-risk-item-title">A clear summary of all insurance requirements</div></li>
              <li><div className="bi-risk-item-title">Gap analysis against your current coverage</div></li>
              <li><div className="bi-risk-item-title">Specific policy or endorsement recommendations</div></li>
              <li><div className="bi-risk-item-title">Certificates of insurance structured to satisfy the contract</div></li>
              <li><div className="bi-risk-item-title">Additional insured endorsements where required</div></li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--light">
      <div className="bi-industry-inner">
        <h3>Common contract scenarios we handle</h3>
        <div className="bi-scenarios">
          <div className="bi-scenario">
            <div className="bi-scenario-label">Scenario</div>
            <h4>Commercial Lease</h4>
            <p><strong>Requirement:</strong> Landlord requires GL with $2M aggregate, tenant as named insured, and landlord listed as additional insured before move-in.</p>
            <p><strong>What we do:</strong> Structure your policy to meet those exact specs, issue the COI, and send the additional insured endorsement directly to the landlord — often within 24 hours.</p>
          </div>
          <div className="bi-scenario">
            <div className="bi-scenario-label">Scenario</div>
            <h4>Enterprise MSA / Vendor Onboarding</h4>
            <p><strong>Requirement:</strong> Fortune 500 client requires Tech E&amp;O ($2M), Cyber ($1M), GL ($1M/$2M), primary/non-contributory, and a 30-day cancellation notice.</p>
            <p><strong>What we do:</strong> Review the insurance exhibit, close any coverage gaps, issue a compliant COI, and ensure all required endorsements are in place before you sign.</p>
          </div>
          <div className="bi-scenario">
            <div className="bi-scenario-label">Scenario</div>
            <h4>VC Term Sheet / Series A</h4>
            <p><strong>Requirement:</strong> Lead investor requires D&amp;O coverage effective at close, with a minimum limit and proof of coverage for the closing call.</p>
            <p><strong>What we do:</strong> Bind D&amp;O coverage that satisfies the investor's requirements and provide the policy documentation needed to satisfy diligence — on your timeline, not the insurer's.</p>
          </div>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--muted">
      <div className="bi-industry-inner">
        <h3>Frequently asked questions</h3>
        <div className="bi-faq">
          <div className="bi-faq-item">
            <h4>What does "additional insured" mean?</h4>
            <p>An additional insured is a party added to your insurance policy who receives certain protections under your coverage. Landlords, clients, and investors frequently require this. BizServe can add additional insureds and issue the corresponding endorsement quickly.</p>
          </div>
          <div className="bi-faq-item">
            <h4>What is "primary and non-contributory"?</h4>
            <p>"Primary" means your policy responds before any other insurance the additional insured may have. "Non-contributory" means your insurer won't seek contribution from the additional insured's own policies. Together, these terms are standard in enterprise vendor agreements.</p>
          </div>
          <div className="bi-faq-item">
            <h4>How fast can you review a contract?</h4>
            <p>Standard contract reviews are typically completed within one business day. For time-sensitive situations — like a deal closing this week — BizServe prioritizes your review to ensure coverage is in place before the deadline.</p>
          </div>
          <div className="bi-faq-item">
            <h4>What if my current policy doesn't meet the contract requirements?</h4>
            <p>We identify the gaps and provide specific recommendations to bring your coverage into compliance — whether that means increasing limits, adding an endorsement, or binding a new policy. We can often have compliant coverage in place within 24–48 hours.</p>
          </div>
        </div>
      </div>
    </section>

    <CTA />
  </>
);

const WorkforceRiskPage = ({ onBack }: { onBack?: () => void }) => (
  <>
    <Hero
      showBack
      onBack={onBack}
      title="Protect your business as your team grows."
      subtitle="Every hire introduces new risk. BizServe helps you manage workforce risk proactively — with employment practices coverage, access to a 24/7 legal hotline, and expert guidance before disputes become costly claims."
      primaryCta="Get EPL coverage"
      secondaryCta="Talk to an advisor"
    />
    <section className="bi-industry-section bi-industry-section--light">
      <div className="bi-industry-inner">
        <h3>The risks that come with hiring</h3>
        <ul className="bi-risk-list">
          <li>
            <div className="bi-risk-item-title">Wrongful termination</div>
            <div className="bi-risk-item-body">Dismissing an employee — even for legitimate performance reasons — can trigger claims of wrongful termination if the process isn't documented properly or if the employee believes protected activity was a factor.</div>
          </li>
          <li>
            <div className="bi-risk-item-title">Harassment and discrimination</div>
            <div className="bi-risk-item-body">Claims of workplace harassment or discrimination based on protected characteristics can arise at any stage of the employment relationship — during hiring, promotion, or termination. These claims can be expensive to defend even when unfounded.</div>
          </li>
          <li>
            <div className="bi-risk-item-title">Contentious departures</div>
            <div className="bi-risk-item-body">High-growth companies face particular risk during layoffs, restructurings, and founder departures. Contentious exits — especially involving equity or non-compete disputes — frequently result in legal action.</div>
          </li>
          <li>
            <div className="bi-risk-item-title">Contractor misclassification</div>
            <div className="bi-risk-item-body">Classifying workers as independent contractors when they should be employees can trigger state labor investigations, back-tax liability, and civil suits — even if the misclassification was unintentional.</div>
          </li>
        </ul>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--muted">
      <div className="bi-industry-inner">
        <h3>The EPL Legal Hotline — early advice, lower costs</h3>
        <div className="bi-industry-two-col">
          <div>
            <h4 style={{ fontWeight: 700, color: '#0f172a', marginBottom: '10px' }}>24/7 access to employment counsel</h4>
            <p className="bi-industry-body">Employee problems are the #1 source of startup claims. The BizServe legal hotline gives you immediate access to top-tier employment law attorneys — including firms like Gordon Rees — when an incident occurs. Policyholders receive three free hours of employment law advice to help address issues before they escalate into formal claims.</p>
          </div>
          <div>
            <h4 style={{ fontWeight: 700, color: '#0f172a', marginBottom: '10px' }}>Save at least 25% on out-of-pocket costs</h4>
            <p className="bi-industry-body">Using the legal hotline early — before a workplace dispute becomes a formal claim — can reduce your out-of-pocket costs by at least 25% (where a retention reduction endorsement is active). The advice you get on day one of an incident is often the most valuable — and the most cost-effective — investment you can make.</p>
          </div>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--light">
      <div className="bi-industry-inner">
        <h3>Coverage that supports your HR function</h3>
        <div className="bi-coverage-grid">
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">EPL</div>
            <h4>Employment Practices Liability</h4>
            <p>Covers legal defense costs and settlements for wrongful termination, discrimination, harassment, and wage disputes — for employees, contractors, and job applicants.</p>
          </div>
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">D&amp;O</div>
            <h4>Directors &amp; Officers</h4>
            <p>Protects founders and executives from personal liability when leadership decisions related to staffing, compensation, or corporate governance result in legal action.</p>
          </div>
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Bundle</div>
            <h4>Management Liability</h4>
            <p>Bundles EPL, D&amp;O, Fiduciary, and Crime in one policy — providing comprehensive protection for your leadership team and your workforce under one renewal date.</p>
          </div>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--muted">
      <div className="bi-industry-inner">
        <h3>Frequently asked questions</h3>
        <div className="bi-faq">
          <div className="bi-faq-item">
            <h4>What is the EPL Legal Hotline?</h4>
            <p>The hotline gives you immediate access to qualified employment law attorneys when a workplace incident arises. Three free hours of legal advice are included with your EPL policy — helping you resolve issues early before they become formal, costly claims.</p>
          </div>
          <div className="bi-faq-item">
            <h4>When should I call the hotline?</h4>
            <p>As soon as a workplace incident occurs — not after it has escalated. Employee problems are the #1 source of startup claims. Early legal advice dramatically improves your ability to resolve the situation efficiently and cost-effectively.</p>
          </div>
          <div className="bi-faq-item">
            <h4>Does EPL cover independent contractors?</h4>
            <p>Most EPL policies cover claims from contractors as well as W-2 employees. Given the prevalence of contractor misclassification disputes, this is an important coverage feature for companies with mixed workforces.</p>
          </div>
          <div className="bi-faq-item">
            <h4>How does EPL interact with D&amp;O?</h4>
            <p>EPL covers employment relationship claims; D&amp;O covers management decisions in the corporate governance context. When a leadership decision triggers both an employment claim and an investor or board dispute, having both coverages is essential — and the Management Liability bundle provides both.</p>
          </div>
        </div>
      </div>
    </section>

    <CTA />
  </>
);

const ClaimsPage = ({ onBack }: { onBack?: () => void }) => (
  <>
    <Hero
      showBack
      onBack={onBack}
      title="When things go wrong, you won't face it alone."
      subtitle="BizServe's claims advocacy service means you have a dedicated team fighting in your corner — with empathy, integrity, expert counsel, and direct access to your claims manager from first report to final resolution."
      primaryCta="File a claim"
      secondaryCta="Talk to a claims advisor"
    />
    <section className="bi-industry-section bi-industry-section--light">
      <div className="bi-industry-inner">
        <h3>Why claims advocacy matters</h3>
        <div className="bi-coverage-grid">
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Advocacy</div>
            <h4>Client Advocacy</h4>
            <p>We approach every claim with empathy and integrity. You have direct access to a claims expert from the moment you report — not a call center, and not a black box.</p>
          </div>
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Communication</div>
            <h4>Clear Communication</h4>
            <p>Your claims manager provides timely, transparent updates throughout the lifecycle of your claim — so you always know where things stand and what happens next.</p>
          </div>
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Outcomes</div>
            <h4>Fair Outcomes</h4>
            <p>Coverage is interpreted consistently and broadly. We work with a trusted panel of attorneys who have startup expertise and a mandate to achieve fair, reasonable outcomes.</p>
          </div>
          <div className="bi-coverage-card">
            <div className="bi-coverage-chip">Counsel</div>
            <h4>Expert Counsel</h4>
            <p>BizServe connects you with top-tier attorneys specializing in employment law, intellectual property, data privacy, and cyber — depending on the nature of your claim.</p>
          </div>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--muted">
      <div className="bi-industry-inner">
        <h3>The lifecycle of a claim</h3>
        <div className="bi-industry-two-col">
          <ul className="bi-risk-list">
            <li>
              <div className="bi-risk-item-title">Step 1 — Report your claim</div>
              <div className="bi-risk-item-body">Submit your claim through your member account or directly via our claims form. Attach all available documentation — demand letters, police reports, breach notices, or any correspondence from the claimant.</div>
            </li>
            <li>
              <div className="bi-risk-item-title">Step 2 — Introduction</div>
              <div className="bi-risk-item-body">A dedicated claims manager is assigned and contacts you within 1–2 business days to introduce themselves, explain the process, and answer your initial questions.</div>
            </li>
            <li>
              <div className="bi-risk-item-title">Step 3 — Coverage analysis</div>
              <div className="bi-risk-item-body">Your claims manager documents how your coverage applies to the specific incident — identifying which policies respond, applicable limits, and any relevant deductibles or retentions.</div>
            </li>
          </ul>
          <ul className="bi-risk-list">
            <li>
              <div className="bi-risk-item-title">Step 4 — Collaborate on a defense</div>
              <div className="bi-risk-item-body">Where legal defense is warranted, your claims manager helps retain qualified counsel from the panel and develops a defense strategy with you — so you're never navigating the litigation process alone.</div>
            </li>
            <li>
              <div className="bi-risk-item-title">Step 5 — Resolution</div>
              <div className="bi-risk-item-body">Your claim is resolved through settlement, judgment, or dismissal. Covered costs are paid by ACH, wire, or check — up to your applicable policy limits. Most property claims are reviewed and reimbursed within 2 business days.</div>
            </li>
          </ul>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--light">
      <div className="bi-industry-inner">
        <h3>Cyber incident response — 4-hour guarantee, 24/7/365</h3>
        <div className="bi-industry-two-col">
          <div>
            <h4 style={{ fontWeight: 700, color: '#0f172a', marginBottom: '10px' }}>Immediate response when it counts</h4>
            <p className="bi-industry-body">BizServe partners with specialized cyber breach coaches who handle the majority of U.S. cyber incidents reported to insurers. When a cyber incident is reported, a response begins within 4 hours — 24 hours a day, 7 days a week, 365 days a year. Speed matters: the first hours after a breach are critical to limiting damage.</p>
          </div>
          <div>
            <h4 style={{ fontWeight: 700, color: '#0f172a', marginBottom: '10px' }}>End-to-end incident management</h4>
            <p className="bi-industry-body">The cyber breach response team takes the lead on forensics (identifying how the breach occurred), public relations (managing external communications), breach notification (identifying and notifying affected individuals), and remediation — so you can focus on your customers and your business.</p>
          </div>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--muted">
      <div className="bi-industry-inner">
        <h3>Employment practices incidents — the EPL hotline</h3>
        <div className="bi-industry-two-col">
          <div>
            <h4 style={{ fontWeight: 700, color: '#0f172a', marginBottom: '10px' }}>Employee problems are the #1 source of claims</h4>
            <p className="bi-industry-body">Contentious departures, harassment allegations, discrimination complaints — employment issues are consistently the most common trigger of startup insurance claims. BizServe's EPL hotline (+1 844-488-6728) gives you immediate access to qualified employment law attorneys when an incident occurs.</p>
          </div>
          <div>
            <h4 style={{ fontWeight: 700, color: '#0f172a', marginBottom: '10px' }}>Three free hours + 25% cost reduction</h4>
            <p className="bi-industry-body">Policyholders receive three free hours of employment law advice through the hotline. Using this service early — before a dispute becomes a formal claim — can reduce your out-of-pocket costs by at least 25% (where a retention reduction endorsement is active). Early advice is the most cost-effective claims management tool available.</p>
          </div>
        </div>
      </div>
    </section>

    <section className="bi-industry-section bi-industry-section--light">
      <div className="bi-industry-inner">
        <h3>Frequently asked questions</h3>
        <div className="bi-faq">
          <div className="bi-faq-item">
            <h4>How do I report a claim?</h4>
            <p>Log into your member account and select "File a Claim," or submit directly via our claims form. Attach all available documentation — demand letters, police reports, breach notices, or any relevant correspondence from the claimant or regulatory body.</p>
          </div>
          <div className="bi-faq-item">
            <h4>What happens after I report a claim?</h4>
            <p>A dedicated claims manager is assigned to your case and will contact you within 1–2 business days to introduce themselves and walk you through the process. 100% of claims are reviewed by BizServe's Head of Claims.</p>
          </div>
          <div className="bi-faq-item">
            <h4>How long does a claim take to resolve?</h4>
            <p>Property and theft claims are often resolved within days to a month. Lawsuits and demand letters take longer depending on complexity. Your claims manager will keep you informed throughout and work with you on strategy to achieve resolution efficiently.</p>
          </div>
          <div className="bi-faq-item">
            <h4>How are claim payments made?</h4>
            <p>Covered claim payments are made by ACH, wire transfer, or check — depending on carrier and claim type. Most standard property claims are reviewed and reimbursed within 2 business days.</p>
          </div>
          <div className="bi-faq-item">
            <h4>What does a claims manager do for me?</h4>
            <p>Your claims manager handles payment coordination, status updates, attorney retention, coverage review, and document gathering on your behalf — so you can focus on running your business while the claim is being managed.</p>
          </div>
        </div>
      </div>
    </section>

    <CTA />
  </>
);

const BusinessInsurance = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const segments = location.pathname.split("/").filter(Boolean);
  const baseSegments = segments.slice(0, 2);
  const basePath = `/${baseSegments.join("/")}`;

  const pathSlug = segments.length > 2 ? segments[segments.length - 1].toLowerCase() : "";
  const paramSlug = (searchParams.get("industry") || "").toLowerCase();
  const coverageSlug = (searchParams.get("coverage") || "").toLowerCase();
  const serviceSlug = (searchParams.get("service") || "").toLowerCase();

  // Prefer query param; if none but a path slug exists, migrate to ?industry=slug
  React.useEffect(() => {
    if (!paramSlug && pathSlug) {
      const next = new URLSearchParams(searchParams);
      next.set("industry", pathSlug);
      navigate(`${basePath}?${next.toString()}`, { replace: true });
    }
  }, [paramSlug, pathSlug, basePath, navigate, searchParams]);

  const slug = (paramSlug || pathSlug || "").toLowerCase();

  const handleSelectVertical = (verticalSlug: string) => {
    if (!verticalSlug) return;
    const next = new URLSearchParams(searchParams);
    next.set("industry", verticalSlug.toLowerCase());
    navigate(`${basePath}?${next.toString()}`);
  };

  const handleSelectCoverage = (s: string) => {
    if (!s) return;
    const next = new URLSearchParams(searchParams);
    next.set("coverage", s.toLowerCase());
    navigate(`${basePath}?${next.toString()}`);
  };

  const handleSelectService = (s: string) => {
    if (!s) return;
    const next = new URLSearchParams(searchParams);
    next.set("service", s.toLowerCase());
    navigate(`${basePath}?${next.toString()}`);
  };

  const handleBackToOverview = () => {
    navigate(basePath);
  };

  const renderContent = () => {
    if (coverageSlug) {
      switch (coverageSlug) {
        case "general-liability":    return <GeneralLiabilityPage    onBack={handleBackToOverview} />;
        case "bop":                  return <BopPage                 onBack={handleBackToOverview} />;
        case "workers-compensation": return <WorkersCompPage         onBack={handleBackToOverview} />;
        case "cyber":                return <CyberPage               onBack={handleBackToOverview} />;
        case "errors-omissions":     return <EoPage                  onBack={handleBackToOverview} />;
        case "directors-officers":   return <DAndOPage               onBack={handleBackToOverview} />;
        case "management-liability": return <ManagementLiabilityPage onBack={handleBackToOverview} />;
        case "employment-practices": return <EmploymentPracticesPage onBack={handleBackToOverview} />;
        case "ai-insurance":         return <AiInsurancePage         onBack={handleBackToOverview} />;
      }
    }
    if (serviceSlug) {
      switch (serviceSlug) {
        case "contract-reviews": return <ContractReviewsPage onBack={handleBackToOverview} />;
        case "workforce-risk":   return <WorkforceRiskPage   onBack={handleBackToOverview} />;
        case "claims":           return <ClaimsPage          onBack={handleBackToOverview} />;
      }
    }
    if (!slug) {
      return <OverviewPage onSelectVertical={handleSelectVertical} onSelectCoverage={handleSelectCoverage} onSelectService={handleSelectService} />;
    }
    switch (slug) {
      case "tech-software":
        return <TechSoftwarePage onBack={handleBackToOverview} />;
      case "ecomm-digital":
        return <EcommDigitalPage onBack={handleBackToOverview} />;
      case "professional-services":
        return <ProfessionalServicesPage onBack={handleBackToOverview} />;
      case "freelancers-solopreneurs":
        return <FreelancersSolopreneursPage onBack={handleBackToOverview} />;
      default:
        return <OverviewPage onSelectVertical={handleSelectVertical} onSelectCoverage={handleSelectCoverage} onSelectService={handleSelectService} />;
    }
  };

  return (
    <div className="bi-page">
      <style>{styles}</style>
      {renderContent()}
    </div>
  );
};

export default BusinessInsurance;
